import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion, useTransform, useMotionValue } from 'motion/react';
import ReactDOM from 'react-dom';
import SektionSidePanel from './SektionSidePanel';
import Bewerbungsmappe from './Bewerbungsmappe';
import { generateAndDownloadPDF } from '../utils/pdfGenerate';
import CVVorschau from './CVVorschau';
import AnschreibenVorschau, { AnschreibenAbsatzPanel, AnschreibenAbsenderPanel, AnschreibenEmpfaengerPanel, AnschreibenDatumPanel, parseAnschreiben } from './AnschreibenVorschau';
import { inferTemplateId } from '../templates';
import { useConfirm } from './ConfirmDialog';
import {
  getProfile, getProfil, getCVVorlagen, getAnschreibenVorlagen,
  starteAnalyse, starteGenerierung, updateStelle,
  getStellen, extractStellenPdf, fetchStelleText, jobAbbrechen,
} from '../api';
import { useJobs, useJobForStelle } from '../contexts/JobsContext';
import { capture } from '../tracking/posthog';
import './StellenEditor.css';
import KeywordPill from './KeywordPill';
import ZoomRegler, { zoomKlemmen } from './ZoomRegler';
import { buildFitVerdict, fitScoreLabel } from '../utils/fitScores';
import { gehoertZurFolge } from '../utils/anLayout';

// eslint-disable-next-line no-unused-vars
const InfoIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_info)">
      <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 8V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M11.9945 16H12.0035" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </g>
    <defs>
      <clipPath id="clip0_info"><rect width="24" height="24" fill="white"/></clipPath>
    </defs>
  </svg>
);

const FileTextIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2.75H7.75A2.75 2.75 0 0 0 5 5.5v13A2.75 2.75 0 0 0 7.75 21.25h8.5A2.75 2.75 0 0 0 19 18.5V8L14 2.75Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M14 3v4.3A1.7 1.7 0 0 0 15.7 9H19" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
    <path d="M8.75 13h6.5M8.75 16h4.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

const LinkIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.6 14.4 14.4 9.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M10.7 6.1 12 4.8a4.35 4.35 0 0 1 6.15 6.15l-1.3 1.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="m13.3 17.9-1.3 1.3a4.35 4.35 0 0 1-6.15-6.15l1.3-1.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const UploadIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 15.5V4.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="m7.75 9 4.25-4.25L16.25 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 14.75v3A2.25 2.25 0 0 0 7.25 20h9.5A2.25 2.25 0 0 0 19 17.75v-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const SaveIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 5.75A1.75 1.75 0 0 1 6.75 4h8.65L19 7.6v10.65A1.75 1.75 0 0 1 17.25 20H6.75A1.75 1.75 0 0 1 5 18.25V5.75Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
    <path d="M8 4v5h7V4.35M8.5 20v-5.25h7V20" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
const DropdownChevronIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.92 8.9502L13.4 15.4702C12.63 16.2402 11.37 16.2402 10.6 15.4702L4.07996 8.9502" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SendeIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_sende)">
      <path d="M9.51002 4.23062L18.07 8.51062C21.91 10.4306 21.91 13.5706 18.07 15.4906L9.51002 19.7706C3.75002 22.6506 1.40002 20.2906 4.28002 14.5406L5.15002 12.8106C5.37002 12.3706 5.37002 11.6406 5.15002 11.2006L4.28002 9.46062C1.40002 3.71062 3.76002 1.35062 9.51002 4.23062Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.43994 12H10.8399" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="clip0_sende"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

const FilterIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.5002 14.9902L15.4902 20.0102" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.5 14.9902H20.5" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.5 9.01023L8.51 3.99023" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20.5 9.00977H3.5" stroke={color} strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AngefertigtIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_angefertigt)">
      <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" fill={color} />
      <path d="M18 15.25C17.59 15.25 17.25 14.91 17.25 14.5C17.25 13.81 16.69 13.25 16 13.25H8C7.31 13.25 6.75 13.81 6.75 14.5C6.75 14.91 6.41 15.25 6 15.25C5.59 15.25 5.25 14.91 5.25 14.5V7.75C5.25 6.23 6.48 5 8 5H16C17.52 5 18.75 6.23 18.75 7.75V14.5C18.75 14.91 18.41 15.25 18 15.25ZM8 11.75H16C16.45 11.75 16.88 11.86 17.25 12.05V7.75C17.25 7.06 16.69 6.5 16 6.5H8C7.31 6.5 6.75 7.06 6.75 7.75V12.05C7.12 11.86 7.55 11.75 8 11.75Z" fill={color} />
      <path d="M19 16.5H18C17.59 16.5 17.25 16.16 17.25 15.75C17.25 15.34 17.59 15 18 15H19C19.41 15 19.75 15.34 19.75 15.75C19.75 16.16 19.41 16.5 19 16.5Z" fill={color} />
      <path d="M6 16.5H5C4.59 16.5 4.25 16.16 4.25 15.75C4.25 15.34 4.59 15 5 15H6C6.41 15 6.75 15.34 6.75 15.75C6.75 16.16 6.41 16.5 6 16.5Z" fill={color} />
      <path d="M18 14.75C17.59 14.75 17.25 14.41 17.25 14V11C17.25 10.31 16.69 9.75 16 9.75H8C7.31 9.75 6.75 10.31 6.75 11V14C6.75 14.41 6.41 14.75 6 14.75C5.59 14.75 5.25 14.41 5.25 14V11C5.25 9.48 6.48 8.25 8 8.25H16C17.52 8.25 18.75 9.48 18.75 11V14C18.75 14.41 18.41 14.75 18 14.75Z" fill={color} />
      <path d="M12 19C10.47 19 9.18003 17.93 8.84003 16.5H6C5.59 16.5 5.25 16.16 5.25 15.75V14.5C5.25 12.98 6.48 11.75 8 11.75H16C17.52 11.75 18.75 12.98 18.75 14.5V15.75C18.75 16.16 18.41 16.5 18 16.5H15.16C14.82 17.93 13.53 19 12 19ZM6.75 15H9.5C9.91 15 10.25 15.34 10.25 15.75C10.25 16.71 11.04 17.5 12 17.5C12.96 17.5 13.75 16.71 13.75 15.75C13.75 15.34 14.09 15 14.5 15H17.25V14.5C17.25 13.81 16.69 13.25 16 13.25H8C7.31 13.25 6.75 13.81 6.75 14.5V15Z" fill={color} />
    </g>
    <defs><clipPath id="clip0_angefertigt"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

const AbsageIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_absage)">
      <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.17004 14.8299L14.83 9.16992" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14.83 14.8299L9.17004 9.16992" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="clip0_absage"><rect width="24" height="24" fill="white"/></clipPath></defs>
  </svg>
);

const ZusageIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.75 12L10.58 14.83L16.25 9.17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const STATUS_OPTIONEN = [
  { key: 'unterlagen_angefertigt', label: 'Angefertigt',    color: '#6b7280', Icon: AngefertigtIcon },
  { key: 'abgeschickt',           label: 'Abgeschickt',    color: '#0284c7', Icon: SendeIcon },
  { key: 'kennenlerngespraech',   label: 'Interview',      color: '#d97706', Icon: FilterIcon },
  { key: 'zusage',                label: 'Zusage!',        color: '#059669', Icon: ZusageIcon },
  { key: 'absage',                label: 'Absage',         color: '#dc2626', Icon: AbsageIcon },
];

const EMPFAENGER_POPUPS = [
  { key: 'name',                      label: 'Firmenname',     msg: 'Nicht gefunden – bitte manuell ergänzen', severity: 'error' },
  { key: 'empfaenger_strasse',        label: 'Adresse',        msg: 'Nicht gefunden – bitte manuell ergänzen', severity: 'error' },
  { key: 'empfaenger_ansprechperson', label: 'Ansprechperson', msg: 'Nicht gefunden – optional ergänzen',      severity: 'warn'  },
];

function extractTitelAusText(text) {
  const zeile = text.split('\n').map(l => l.trim()).find(l => l.length > 5 && l.length < 120);
  if (!zeile) return null;
  return zeile.replace(/\s*[-|]?\s*(Indeed\.com|Stepstone\.de|LinkedIn|XING|Glassdoor|Karriere\.at|Monster\.de).*$/i, '').trim() || null;
}

// ── Analyse Text Parser ───────────────────────────────────
function stripToolParameterMarkup(value) {
  return String(value || '')
    .replace(/<\s*parameter\s+name=["'][^"']+["']\s*>/gi, '')
    .replace(/<\s*\/\s*parameter\s*>/gi, '')
    .replace(/<\s*parameter[^>]*>/gi, '')
    .replace(/<\s*\/?\s*tool[^>]*>/gi, '')
    .trim();
}

function cleanAnalyseItem(line) {
  const cleaned = stripToolParameterMarkup(line)
    .trim()
    .replace(/^(?:[+\-•*]|\d+[.)])\s*/, '')
    .trim();
  // Rausfiltern: Trennlinien (---, ----, ===), leere Platzhalter
  if (/^[-=]{2,}$/.test(cleaned)) return '';
  return cleaned;
}

function renderAnalyseItem(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    return boldMatch ? <strong key={index}>{boldMatch[1]}</strong> : part;
  });
}

function textAusAnalyseWert(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return cleanAnalyseItem(String(value));
  if (typeof value === 'object') {
    for (const key of ['item', 'text', 'punkt', 'beschreibung', 'titel', 'name', 'begriff', 'keyword', 'value', 'grund']) {
      if (String(value[key] || '').trim()) return cleanAnalyseItem(String(value[key]));
    }
  }
  return '';
}

function analyseTextListe(value, fallback = []) {
  const quelle = value == null ? fallback : value;

  const ausString = (str) => {
    const trimmed = str.trim();
    if (trimmed.startsWith('[')) {
      // try standard JSON first, then with normalised curly quotes
      const attempts = [trimmed, trimmed.replace(/[\u201C\u201D\u201E\u201F]/g, '"')];
      for (const attempt of attempts) {
        try {
          const p = JSON.parse(attempt);
          if (Array.isArray(p)) return p.map(textAusAnalyseWert).filter(Boolean);
        } catch (_) {}
      }
      // last resort: strip outer brackets and split on `, "` patterns
      const inner = trimmed.slice(1, -1).trim();
      const items = inner.split(/,\s*[\u201C\u201D"]/).map(s => s.replace(/^[\u201C\u201D"]|[\u201C\u201D"]\s*$/g, '').trim()).filter(Boolean);
      if (items.length > 1) return items.map(textAusAnalyseWert).filter(Boolean);
    }
    // Haiku packt manchmal mehrere Stärken in einen String, getrennt durch " – "
    const cleaned = cleanAnalyseItem(str);
    if (cleaned.length > 120 && cleaned.includes(' – ')) {
      return cleaned.split(' – ').map(s => s.trim()).filter(Boolean);
    }
    return [cleaned].filter(Boolean);
  };

  if (Array.isArray(quelle)) {
    return quelle.flatMap((el) => {
      if (typeof el === 'string') return ausString(el);
      const t = textAusAnalyseWert(el);
      return t ? [t] : [];
    });
  }
  if (typeof quelle === 'string') {
    const trimmed = quelle.trim();
    if (trimmed.startsWith('[')) {
      try {
        const p = JSON.parse(trimmed);
        if (Array.isArray(p)) return p.map(textAusAnalyseWert).filter(Boolean);
      } catch (_) {}
    }
    return quelle.split('\n').map(cleanAnalyseItem).filter(Boolean);
  }
  if (typeof quelle === 'object') {
    return Object.values(quelle).map(textAusAnalyseWert).filter(Boolean);
  }
  return [];
}


// eslint-disable-next-line no-unused-vars
function analyseText(value, fallback = '') {
  const text = textAusAnalyseWert(value);
  return text || String(fallback || '');
}

function parseAnalyse(text) {
  if (!text) return { prozent: null, staerken: [], keywords: [], schwaachen: [], empfehlung: '' };

  const matchProz = text.match(/Übereinstimmung:\s*\[?(\d+)\]?\s*\/\s*10/i);
  const prozent = matchProz ? parseInt(matchProz[1], 10) * 10 : null;

  const staerkenMatch = text.match(/Stärken[^:]*:([\s\S]*?)(?=Relevante Keywords|Was fehlt|Empfehlung|$)/i);
  const staerken = staerkenMatch
    ? staerkenMatch[1].split('\n')
        .map(cleanAnalyseItem)
        .filter(Boolean)
    : [];

  const keywordsMatch = text.match(/Relevante Keywords[^:]*:([\s\S]*?)(?=Was fehlt|Empfehlung|$)/i);
  const keywords = keywordsMatch
    ? keywordsMatch[1].split('\n')
        .map(cleanAnalyseItem)
        .filter(Boolean)
    : [];

  const schwächenMatch = text.match(/Was fehlt[^:]*:([\s\S]*?)(?=Empfehlung|$)/i);
  const schwaachen = schwächenMatch
    ? schwächenMatch[1].split('\n')
        .map(cleanAnalyseItem)
        .filter(Boolean)
    : [];

  const empfehlungMatch = text.match(/Empfehlung[^:]*:([\s\S]*?)$/i);
  const empfehlung = empfehlungMatch ? empfehlungMatch[1].trim() : '';

  return { prozent, staerken, keywords, schwaachen, empfehlung };
}

// ── CV-Text extrahieren für Keyword-Abgleich ─────────────
function cvTextExtrahieren(stelle) {
  if (!stelle) return '';
  if (stelle.cv_rohtext) return stelle.cv_rohtext;
  if (stelle.cv_data) {
    function deepText(obj) {
      if (!obj) return '';
      if (typeof obj === 'string') return obj;
      if (Array.isArray(obj)) return obj.map(deepText).join(' ');
      if (typeof obj === 'object') return Object.values(obj).map(deepText).join(' ');
      return String(obj);
    }
    return deepText(stelle.cv_data.sektionen || stelle.cv_data);
  }
  return '';
}


// ── Schritt → Fortschritt ─────────────────────────────────
function schrittZuProzent(schritt) {
  if (!schritt) return 15;
  if (schritt.startsWith('1/')) return 33;
  if (schritt.startsWith('2/')) return 60;
  if (schritt.startsWith('3/')) return 85;
  return 20;
}

// ── CV Thumbnail (CSS-drawn) ──────────────────────────────
function CvThumbnail({ vorlage }) {
  const key = vorlage?.toLowerCase() || '';
  const isModern = key.includes('modern') || key.includes('mod');
  const isStandard = key.includes('standard');
  const isProfessionell = key.includes('professionell') || key.includes('prof');

  if (isProfessionell) {
    return (
      <div className="se-cv-thumb se-cv-thumb-professionell">
        <div className="se-thumb-pf-header">
          <div className="se-thumb-pf-links">
            <div className="se-thumb-pf-label" />
            <div className="se-thumb-pf-name" />
            <div className="se-thumb-pf-rule" />
            <div className="se-thumb-pf-kontakt">
              <div className="se-thumb-pf-krow" />
              <div className="se-thumb-pf-krow" />
              <div className="se-thumb-pf-krow" />
            </div>
          </div>
          <div className="se-thumb-pf-block" />
        </div>
        <div className="se-thumb-pf-body">
          <div className="se-thumb-pf-section-title" />
          <div className="se-thumb-standard-entry">
            <div className="se-thumb-standard-date" />
            <div className="se-thumb-standard-body">
              <div className="se-thumb-line" />
              <div className="se-thumb-line se-thumb-line-sm" />
            </div>
          </div>
          <div className="se-thumb-standard-entry">
            <div className="se-thumb-standard-date" />
            <div className="se-thumb-standard-body">
              <div className="se-thumb-line" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isStandard) {
    return (
      <div className="se-cv-thumb se-cv-thumb-standard">
        <div className="se-thumb-content se-thumb-center">
          <div className="se-thumb-standard-header" />
          <div className="se-thumb-standard-rule" />
          <div className="se-thumb-standard-entry">
            <div className="se-thumb-standard-date" />
            <div className="se-thumb-standard-body">
              <div className="se-thumb-line" />
              <div className="se-thumb-line se-thumb-line-sm" />
            </div>
          </div>
          <div className="se-thumb-standard-entry">
            <div className="se-thumb-standard-date" />
            <div className="se-thumb-standard-body">
              <div className="se-thumb-line" />
            </div>
          </div>
          <div className="se-thumb-gap" />
          <div className="se-thumb-line se-thumb-line-sm" style={{ width: '55%' }} />
        </div>
      </div>
    );
  }

  return (
    <div className={`se-cv-thumb ${isModern ? 'se-cv-thumb-modern' : 'se-cv-thumb-standard'}`}>
      {isModern ? (
        <div className="se-thumb-content">
          <div className="se-thumb-m-name" />
          <div className="se-thumb-m-rule" />
          <div className="se-thumb-line se-thumb-line-sm" />
          <div className="se-thumb-m-rule" />
          <div className="se-thumb-gap" />
          <div className="se-thumb-m-title" />
          <div className="se-thumb-line" />
          <div className="se-thumb-line se-thumb-line-sm" />
        </div>
      ) : (
        <div className="se-thumb-content se-thumb-center">
          <div className="se-thumb-line se-thumb-line-lg" />
          <div className="se-thumb-line se-thumb-line-md" />
          <div className="se-thumb-gap" />
          <div className="se-thumb-line se-thumb-line-sm" />
          <div className="se-thumb-line" />
          <div className="se-thumb-line se-thumb-line-sm" />
        </div>
      )}
    </div>
  );
}

// Dateiname (z.B. "cv_modern") → Template-ID, passend zu inferTemplateId/CvThumbnail
function templateIdOf(dateiname) {
  const k = (dateiname || '').toLowerCase();
  if (k.includes('prof')) return 'professionell';
  if (k.includes('mod')) return 'modern';
  return 'standard';
}

function DokumentePanel({ stelle, cvVorlagen, onVorlageChange, onDownloadCV, onDownloadAN, onBlobCV, onBlobAN, cvLaedt, anLaedt, defaultMappeOffen = false, onAlsAbgeschickt, editSektion, sektionPanelRef, onSaveEdit, onLiveSektion, onPanelClose, onPanelClosingStart, inlineAnItem, stelleId, profil, onSaveAnEdit }) {
  const istLeererLebenslaufEntwurf = stelle?.quelle === 'lebenslauf'
    && !stelle?.cv_data && !stelle?.cv_rohtext && !stelle?.url && !stelle?.text && !stelle?.generiert_am;
  const hatCV = !!(stelle?.cv_data || stelle?.cv_rohtext) || istLeererLebenslaufEntwurf;
  const aktivTemplate = inferTemplateId(stelle);

  return (
    <div className="se-panel se-download-panel">
      <div className="se-panel-hero">
        <div>
          <h3 className="se-doc-title">Bausteine & Export</h3>
        </div>
      </div>

      {hatCV && (
        <>
          {/* Lebenslauf-Vorlage: Klick stylt die Live-Vorschau sofort um */}
          <details className="se-doc-collapse" open>
            <summary className="se-doc-collapse-head">
              <span className="se-doc-collapse-title">Lebenslauf-Vorlagen</span>
              <span className="se-doc-collapse-chevron" aria-hidden="true">▾</span>
            </summary>
            <div className="se-doc-collapse-body">
              <div className="se-cv-cards">
                {(cvVorlagen || []).map((v) => (
                  <div
                    key={v.dateiname}
                    className={`se-cv-card ${templateIdOf(v.dateiname) === aktivTemplate ? 'aktiv' : ''}`}
                    onClick={() => onVorlageChange(v.dateiname)}
                  >
                    <CvThumbnail vorlage={v.dateiname} />
                    <span className="se-cv-card-name">{v.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </details>

        </>
      )}

      {/* Abschnitt-Bearbeiten fest in der Sidebar (kein überlagerndes Panel) */}
      {editSektion && (
        <SektionSidePanel
          inline
          key={editSektion.idx}
          ref={sektionPanelRef}
          sektion={editSektion}
          onSave={onSaveEdit}
          onLiveChange={onLiveSektion}
          onClose={onPanelClose}
          onClosingStart={onPanelClosingStart}
        />
      )}

      {/* Anschreiben KI-Panel dauerhaft in Sidebar wenn Bearbeitungsmodus + Textblock selektiert */}
      {inlineAnItem && (
        <AnschreibenAbsatzPanel
          inline
          key={inlineAnItem.rohzeile}
          item={inlineAnItem}
          anRohtext={stelle?.an_rohtext || ''}
          stelleId={stelleId}
          profil={profil}
          onSave={onSaveAnEdit}
          onClose={() => {}}
          onClosingStart={() => {}}
        />
      )}

      {stelle?.generiert_am && (
        <Bewerbungsmappe
          key={stelle?.id}
          onDownloadCV={onDownloadCV}
          onDownloadAN={onDownloadAN}
          onBlobCV={onBlobCV}
          onBlobAN={onBlobAN}
          cvLaedt={cvLaedt}
          anLaedt={anLaedt}
          stelle={stelle}
          defaultOffen={defaultMappeOffen}
          onAlsAbgeschickt={onAlsAbgeschickt}
        />
      )}

    </div>
  );
}

// ── Status Card ───────────────────────────────────────────
function StatusCard({ status, schritt, schrittIndex, schrittTotal, analyse, analyseStruktur, cvText, onKenntnisseErgaenzen, istTeilgenerierung = false, klappbar = false, startZu = false }) {
  // eslint-disable-next-line no-unused-vars
  const [scoreDetailsOffen, setScoreDetailsOffen] = useState(false);
  // Zuklappen direkt in der Karte (kleiner Pfeil oben rechts). startZu wird beim
  // Moduswechsel synchron übernommen, bleibt aber frei umschaltbar.
  const [zu, setZu] = useState(startZu);
  const [prevStartZu, setPrevStartZu] = useState(startZu);
  if (prevStartZu !== startZu) { setPrevStartZu(startZu); setZu(startZu); }
  const parsed = parseAnalyse(analyse);
  const staerken = [...new Set(analyseTextListe(analyseStruktur?.staerken, parsed.staerken))];
  const schwaachen = [...new Set(analyseTextListe(analyseStruktur?.schwaechen, parsed.schwaachen))];
  const empfehlungItems = (() => {
    const raw = analyseStruktur?.empfehlung?.length ? analyseStruktur.empfehlung : parsed.empfehlung;
    if (!raw) return [];
    if (Array.isArray(raw)) return [...new Set(raw.filter(Boolean))];
    const str = String(raw).trim();
    const numbered = str.match(/\(\d+\)[^(]+/g);
    if (numbered?.length > 1) return [...new Set(numbered.map(s => s.replace(/^\(\d+\)\s*/, '').trim()))];
    return [str];
  })();
  const llmScore = Number(analyseStruktur?.score);
  const prozent = Number.isFinite(llmScore) ? llmScore * 10 : (parsed.prozent ?? null);
  const verdict = buildFitVerdict(analyseStruktur, prozent);
  const chipKeys = new Set(verdict.dimensions.map(d => d.key));
  const scoreRows = verdict.detailRows.filter(row => !chipKeys.has(row.key));
  const scoreLabel = verdict.label || fitScoreLabel(analyseStruktur);
  // Nur fehlende Kenntnisse — vorhanden/teilweise werden nicht angezeigt
  const fehlendKeywords = (analyseStruktur?.relevante_keywords || [])
    .filter(kw => typeof kw === 'object' && kw.match_typ === 'fehlt');

  const fortschritt = analyse
    ? (scoreRows[0]?.percent ?? prozent ?? 70)
    : (schrittIndex && schrittTotal ? Math.round((schrittIndex / schrittTotal) * 100) : schrittZuProzent(schritt));
  const laedt = (status === 'analyse' || status === 'generiert') && !istTeilgenerierung;

  const gesamtBalken = verdict.overall && (
    <div className="se-verdict-overall">
      <div className="se-verdict-overall-head">
        <span className="se-verdict-overall-text">Gesamt</span>
        <span className="se-verdict-overall-value" style={{ color: verdict.overall.color }}>
          {verdict.overall.display}
        </span>
      </div>
      <div className="se-status-bar-track">
        <div
          className="se-status-bar-fill"
          style={{ width: `${verdict.overall.percent}%`, background: verdict.overall.gradient }}
        />
      </div>
    </div>
  );

  return (
    <div className={`se-status-card${laedt ? ' laedt' : ''}${analyse ? ' hat-analyse' : ''}${zu && analyse ? ' ist-zu' : ''}`}>
      {laedt && (
        <svg className="se-status-beam" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect className="se-status-beam-line"
            x="0" y="0" width="100%" height="100%"
            rx="24" ry="24"
            fill="none"
            stroke="#FFBC00"
            strokeWidth="12"
            strokeDasharray="100 1200"
            strokeLinecap="round"
            pathLength="1300"
          />
        </svg>
      )}
      <div
        className={`se-status-top${klappbar && analyse ? ' is-klappbar' : ''}`}
        onClick={klappbar && analyse ? () => setZu(z => !z) : undefined}
        role={klappbar && analyse ? 'button' : undefined}
        aria-label={klappbar && analyse ? (zu ? 'Fit-Analyse aufklappen' : 'Fit-Analyse zuklappen') : undefined}
        title={klappbar && analyse ? (zu ? 'Aufklappen' : 'Zuklappen') : undefined}
      >
        <span className="se-status-label">
          STATUS: {analyse ? 'ABGESCHLOSSEN' : 'FIT-ANALYSE LÄUFT...'}
        </span>
        {klappbar && analyse && (
          <span className={`se-status-collapse${zu ? ' is-zu' : ''}`} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}
      </div>
      {zu && analyse && (
        <div className="se-verdict se-verdict--collapsed" aria-label="Fit-Analyse">
          {gesamtBalken}
        </div>
      )}
      {!zu && (<>
      {analyse ? (
        <div className="se-verdict" aria-label="Fit-Analyse">
          {/* Klartext-Urteil */}
          {scoreLabel && (
            <div className="se-verdict-label">{scoreLabel}</div>
          )}

          {/* Gesamt-Balken */}
          {gesamtBalken}


          {/* Dimensions-Chips */}
          {verdict.dimensions.length > 0 && (
            <div className="se-fit-chips">
              {verdict.dimensions.map(dim => (
                <div
                  key={dim.key}
                  className={`se-fit-chip se-fit-chip--${dim.level}`}
                  data-info={dim.info}
                  aria-label={`${dim.label} ${dim.levelLabel}: ${dim.info}`}
                  tabIndex={0}
                >
                  <span className="se-fit-chip-label">{dim.label}</span>
                  <span className="se-fit-chip-value" style={{ color: dim.color }}>{dim.levelLabel.toUpperCase()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="se-status-bar-row">
            <span className="se-status-bar-label">Fit-Analyse</span>
            <div className="se-status-bar-track">
              <div
                className={`se-status-bar-fill ${laedt ? 'animiert' : ''}`}
                style={{ width: `${fortschritt}%` }}
              />
            </div>
            <span className="se-status-pct">{fortschritt}%</span>
          </div>
          {laedt && (
            <div className="se-status-skel-body" aria-hidden="true">
              {/* Empfehlung-Skeleton */}
              <div className="se-status-skel-rec">
                <span className="se-cv-skel-line se-cv-skel-title" style={{ width: '30%' }} />
                <span className="se-cv-skel-line long" />
                <span className="se-cv-skel-line medium" />
                <span className="se-cv-skel-line" style={{ width: '60%' }} />
              </div>
              {/* Spalten-Skeleton */}
              <div className="se-status-cols">
                {[
                  ['long', 'medium', 'long', 'short'],
                  ['medium', 'long', 'short', 'long'],
                ].map((widths, col) => (
                  <div key={col} className="se-status-col">
                    <span className="se-cv-skel-line se-cv-skel-title se-status-skel-col-title" />
                    <div className="se-status-skel-lines">
                      {widths.map((w, i) => (
                        <span
                          key={i}
                          className={`se-cv-skel-line ${w}`}
                          style={{ animationDelay: `${(col * 4 + i) * 0.11}s` }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {/* Keywords-Skeleton */}
              <div className="se-status-keywords-head">
                <span className="se-cv-skel-line" style={{ width: '60px', height: '10px' }} />
              </div>
              <div className="se-status-keywords">
                {[68, 90, 55, 76, 62, 82].map((w, i) => (
                  <span
                    key={i}
                    className="se-cv-skel-line se-status-skel-pill"
                    style={{ width: w, animationDelay: `${i * 0.09}s` }}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {analyse && (
        <>
        {empfehlungItems.length > 0 && (
          <div className="se-status-recommendation">
            <span>Empfehlung</span>
            <ul>
              {empfehlungItems.slice(0, 3).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </div>
        )}
        <div className="se-status-cols">
          <div className="se-status-col">
            <div className="se-status-col-title se-status-col-title--green">
              Stärken-Profil
            </div>
            <ul className="se-status-list se-status-list--scroll">
              {staerken.slice(0, 3).map((s, i) => (
                <li key={i}>
                  <span className="se-check se-check--plus" aria-hidden="true">+</span>
                  <span>{renderAnalyseItem(s)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="se-status-col">
            <div className="se-status-col-title se-status-col-title--red">
              Optimierungspotential
            </div>
            <ul className="se-status-list se-status-list--scroll">
              {schwaachen.slice(0, 3).map((s, i) => (
                <li key={i}>
                  <span className="se-check se-check--minus" aria-hidden="true">−</span>
                  <span>{renderAnalyseItem(s)}</span>
                </li>
              ))}
              {schwaachen.length === 0 && laedt && (
                <li className="se-status-placeholder">AI wertet zusätzliche Dokumente aus…</li>
              )}
              {schwaachen.length === 0 && !laedt && (
                <li className="se-status-placeholder">Keine Optimierungsvorschläge</li>
              )}
            </ul>
          </div>
        </div>

        {fehlendKeywords.length > 0 && (
          <>
            <div className="se-status-keywords-head">
              <span className="se-status-keywords-eyebrow">Fehlende Kenntnisse</span>
            </div>
            <div className="se-status-keywords" aria-label="Fehlende Kenntnisse">
              {fehlendKeywords.slice(0, 12).map((keyword, index) => (
                <KeywordPill key={index} keyword={keyword} onErgaenzen={onKenntnisseErgaenzen ? (b, ziel) => onKenntnisseErgaenzen(b, ziel) : undefined} />
              ))}
            </div>
          </>
        )}
        </>
      )}

      {!analyse && (
        <div className="se-status-loading">
          <div className="se-pulse" />
          <span>{schritt || 'Wird vorbereitet…'}</span>
        </div>
      )}
      </>)}
    </div>
  );
}

function KompakteStellenKarte({ text, url, modus, stelleName, isGenerating, onAbbrechen, onNeuGenerieren }) {
  const [expanded, setExpanded] = React.useState(false);
  const [neuGenOffen, setNeuGenOffen] = React.useState(false);

  const urlDomain = (() => {
    try { return new URL(url || '').hostname.replace(/^www\./, ''); } catch { return null; }
  })();

  const vorschauTitel = stelleName || urlDomain || 'Stellenbeschreibung';
  const vollText = (text || '').trim();

  return (
    <div className={`se-build-collapsed-job${expanded ? ' is-expanded' : ''}`}>
      <div
        className="se-build-collapsed-header"
        onClick={() => setExpanded(e => !e)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setExpanded(s => !s)}
      >
        <span className={`se-build-collapsed-chevron${expanded ? ' is-open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <div className="se-build-collapsed-copy">
          <span className="se-build-collapsed-kicker">Stellenbeschreibung</span>
          <p className="se-build-collapsed-title">{vorschauTitel}</p>
          {urlDomain && stelleName && <p className="se-build-collapsed-sub">{urlDomain}</p>}
        </div>
        {isGenerating && (
          <button className="se-abort-btn se-build-cancel" onClick={e => { e.stopPropagation(); onAbbrechen(); }}>
            Abbrechen
          </button>
        )}
        {!isGenerating && onNeuGenerieren && (
          <div className="se-neu-gen-gruppe" onClick={e => e.stopPropagation()}>
            {neuGenOffen ? (
              <>
                <button className="se-neu-gen-btn se-neu-gen-btn--close" onClick={() => setNeuGenOffen(false)}>✕</button>
                <button className="se-neu-gen-btn" onClick={() => { setNeuGenOffen(false); onNeuGenerieren('alles'); }}>Alles</button>
                <button className="se-neu-gen-btn" onClick={() => { setNeuGenOffen(false); onNeuGenerieren('nur_cv'); }}>CV</button>
                <button className="se-neu-gen-btn" onClick={() => { setNeuGenOffen(false); onNeuGenerieren('nur_anschreiben'); }}>Anschreiben</button>
              </>
            ) : (
              <button className="se-neu-gen-btn se-neu-gen-btn--haupt" onClick={() => setNeuGenOffen(true)}>Neu generieren ↺</button>
            )}
          </div>
        )}
      </div>
      {expanded && vollText && (
        <div className="se-build-collapsed-body">
          <p>{vollText}</p>
        </div>
      )}
    </div>
  );
}

function CVGenerationSkeleton({ keywords = [], exiting = false }) {
  const sichtbareKeywords = keywords.slice(0, 10);
  const keywordPositions = [
    { x: 14, y: 18, d: '0s' },
    { x: 63, y: 16, d: '0.16s' },
    { x: 38, y: 28, d: '0.32s' },
    { x: 18, y: 43, d: '0.48s' },
    { x: 68, y: 42, d: '0.64s' },
    { x: 43, y: 54, d: '0.8s' },
    { x: 17, y: 69, d: '0.96s' },
    { x: 61, y: 68, d: '1.12s' },
    { x: 35, y: 80, d: '1.28s' },
    { x: 72, y: 84, d: '1.44s' },
  ];

  const line = (className = '') => <span className={`se-cv-skel-line ${className}`} />;

  return (
    <div className={`cv-vorschau-wrapper se-cv-build${exiting ? ' exiting' : ''}`}>
      <div className="cv-vorschau-label">
        <span>Lebenslauf wird gebaut</span>
      </div>
      <div className="cv-paginas-container se-cv-skeleton-container">
        <div className="cv-pagina se-cv-skeleton-page" aria-label="Lebenslauf wird generiert">
          <div className="se-cv-skel-header">
            <div>
              {line('se-cv-skel-name')}
              {line('se-cv-skel-contact')}
            </div>
            <span className="se-cv-skel-photo" />
          </div>

          <div className="se-cv-skel-section se-cv-skel-profile">
            {line('se-cv-skel-title')}
            {line()}
            {line('short')}
          </div>

          <div className="se-cv-skel-grid">
            <div className="se-cv-skel-main">
              {[0, 1, 2].map((item) => (
                <div className="se-cv-skel-section" key={item}>
                  {line('se-cv-skel-title')}
                  {line()}
                  {line('medium')}
                  {line('long')}
                </div>
              ))}
            </div>
            <div className="se-cv-skel-side">
              {[0, 1, 2, 3].map((item) => (
                <div className="se-cv-skel-section compact" key={item}>
                  {line('se-cv-skel-title')}
                  {line('short')}
                  {line('medium')}
                </div>
              ))}
            </div>
          </div>

          {sichtbareKeywords.length > 0 && (
            <div className="se-cv-keyword-layer" aria-hidden="true">
              {sichtbareKeywords.map((keyword, index) => {
                const pos = keywordPositions[index % keywordPositions.length];
                return (
                  <span
                    key={`${keyword}-${index}`}
                    className="se-cv-flying-keyword"
                    style={{ '--kw-x': `${pos.x}%`, '--kw-y': `${pos.y}%`, '--kw-delay': pos.d }}
                  >
                    {keyword}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Hauptkomponente ───────────────────────────────────────
function StellenEditor({ stelle, tonlage, onStelleGeaendert, onKeinKontingent, panelWidth = 320, panelWidthMV: panelWidthMVProp, onKenntnisseErgaenzen, autoAnalyse = false, onAutoAnalyseDone, defaultMappeOffen = false, bearbeitungsmodus = false, bearbeitenButton = null }) {
  const fallbackMV = useMotionValue(panelWidth);
  const activePanelMV = panelWidthMVProp ?? fallbackMV;
  const fabRight = useTransform(activePanelMV, v => v + 60);
  const [modus, setModus] = useState('text'); // 'text' | 'url'
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [textGeladen, setTextGeladen] = useState(false);
  const [pdfLaedt, setPdfLaedt] = useState(false);
  const [aktuellesProfil, setAktuellesProfil] = useState(null);
  const [profilName, setProfilName] = useState('');
  const [cvVorlagen, setCvVorlagen] = useState([]);
  const [cvVorlage, setCvVorlage] = useState('');
  const [anVorlage, setAnVorlage] = useState('');
  const [fehler, setFehler] = useState(null);

  // Ein fehlgeschlagenes Speichern wurde früher überall stillschweigend verschluckt: Die
  // Änderung stand auf dem Bildschirm, der Server wusste nichts davon, und der User hielt
  // sie für gesichert. Jetzt sagt es die Fehlerzeile über dem Dokument.
  const speicherFehler = useCallback(() => {
    setFehler('Die letzte Änderung konnte nicht gespeichert werden. Bitte prüfen Sie Ihre Verbindung und ändern Sie die Stelle erneut.');
  }, []);

  const { registerJob, removeJob } = useJobs();
  const activeJob = useJobForStelle(stelle?.id);

  // Alles Job-bezogene kommt aus dem Context; Fallback auf Stelle-Daten wenn kein Job läuft
  const status = activeJob?.status ?? (stelle?.analyse && !stelle?.generiert_am ? 'warten' : null);
  const schritt = activeJob?.schritt ?? '';
  const schrittIndex = activeJob?.schritt_index ?? null;
  const schrittTotal = activeJob?.schritt_total ?? null;
  const analyse = activeJob?.analyse ?? stelle?.analyse ?? null;
  const analyseStruktur = activeJob?.analyse_struktur ?? stelle?.analyse_struktur ?? null;
  const jobId = activeJob?.jobId ?? stelle?._runningJobId ?? null;
  const [aktivesDok, setAktivesDok] = useState('cv');

  // ── Zoom ─────────────────────────────────────────────────────────────────────
  // `zoom` ist `null`, solange die automatische Einpassung das Sagen hat, und sonst der
  // Maßstab, den der User eingestellt hat. `fits` ist das Gegenstück: der eingepasste
  // Wert, den jede Vorschau selbst ausrechnet und hier meldet. Beides getrennt zu halten
  // ist der Kern — sonst überschriebe die Einpassung bei jeder Fenster-Änderung wieder,
  // was der User eingestellt hat.
  const [zoom, setZoom] = useState(null);
  const [fits, setFits] = useState({ cv: 1, an: 1 });
  const meldeCvFit = useCallback((w) => setFits(f => (f.cv === w ? f : { ...f, cv: w })), []);
  const meldeAnFit = useCallback((w) => setFits(f => (f.an === w ? f : { ...f, an: w })), []);
  const fitSkala = fits[aktivesDok] ?? 1;
  const zeigtSkala = zoom ?? fitSkala;
  // Der Rad-Zuhörer wird nur einmal aufgesetzt und liest den aktuellen Stand hier ab.
  const skalaRef = useRef(1);
  skalaRef.current = zeigtSkala;
  const scrollRef = useRef(null);

  // Strg + Mausrad zoomt das Dokument, nicht die Webseite.
  //
  // Der Browser fragt die Seite zuerst, ob sie das Rad selbst behandelt. Genau das sagt
  // `preventDefault()` — danach vergrößert der Browser nichts mehr. Damit das erlaubt
  // ist, muss der Zuhörer ausdrücklich als „nicht passiv" angemeldet werden; React
  // meldet Rad-Ereignisse von sich aus passiv an, deshalb hier von Hand.
  //
  // Auf dem Mac meldet auch die Zwei-Finger-Kneifgeste am Trackpad `ctrlKey` — die
  // Geste funktioniert dadurch mit, ohne eigenen Code.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const beiRad = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      // Exponentiell statt linear: Ein Rad-Schritt fühlt sich bei 30 % genauso stark an
      // wie bei 200 %. Linear wäre er unten grob und oben zäh.
      setZoom(zoomKlemmen(skalaRef.current * Math.exp(-e.deltaY / 400)));
    };
    el.addEventListener('wheel', beiRad, { passive: false });
    return () => el.removeEventListener('wheel', beiRad);
  }, []);

  // Beim starken Hineinzoomen soll das Blatt in der Mitte bleiben.
  //
  // Der User hat am 2026-08-11 gemeldet, dass es „kurz vor dem Ende nach rechts rückt".
  // Nachgemessen: Bis 130 % wächst das Blatt gleichmäßig nach beiden Seiten, seine linke
  // Kante wandert also nach links. Ab 140 % ist es breiter als sein Kasten — und ein
  // mittiger Rand („margin: auto") gibt genau dann auf. Der Browser setzt das Blatt
  // stattdessen an die linke Innenkante. Weil deren Abstand vom Zoom mitwächst, rückt es
  // mit jeder weiteren Stufe ein Stück weiter nach rechts: gemessen 393 Punkte bei 140 %,
  // 395 bei 150 %. Genau der beschriebene Ruck.
  //
  // Dagegen hilft kein anderer Rand: Sobald etwas breiter ist als sein Kasten, muss die
  // Ausrichtung kippen, sonst wäre die linke Hälfte gar nicht mehr erreichbar. Also nicht
  // das Blatt anders ausrichten, sondern hinsehen, wo es zählt — der Kasten scrollt
  // seitlich ohnehin schon, seine Rollposition wird auf die Mitte gestellt. Für das Auge
  // wächst das Blatt dadurch weiter um seinen Mittelpunkt.
  useEffect(() => {
    if (zoom == null) return;
    const el = scrollRef.current;
    if (!el) return;
    const roller = [...el.querySelectorAll('.an-paginas-container, .cv-paginas-container')]
      .find(k => k.clientWidth > 0 && k.scrollWidth > k.clientWidth);
    if (!roller) return;
    roller.scrollLeft = (roller.scrollWidth - roller.clientWidth) / 2;
  }, [zoom, aktivesDok]);

  const [editSektion, setEditSektion] = useState(null); // { idx, section }
  const [editAnItem, setEditAnItem] = useState(null); // { item, profil, anRohtext }
  const [editAbsender, setEditAbsender] = useState(false);
  const [editEmpfaenger, setEditEmpfaenger] = useState(false);
  const [editDatum, setEditDatum] = useState(false);
  const [selectedAnBlock, setSelectedAnBlock] = useState(null);
  const [baseLayerPushed, setBaseLayerPushed] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [dismissedPopups, setDismissedPopups] = useState(new Set());
  const [statusDropdownOffen, setStatusDropdownOffen] = useState(false);
  const [stampAnzeige, setStampAnzeige] = useState(null);
  const [buildExit, setBuildExit] = useState(false);
  const [buildExitKeywords, setBuildExitKeywords] = useState([]);
  const [textLaedt, setTextLaedt] = useState(false);
  const urlFetchInFlightRef = React.useRef(false);
  const statusFabRef = useRef(null);
  const sektionPanelRef = useRef(null);
  const pendingSektionRef = useRef(null); // Sektion die geöffnet werden soll nach Schließ-Animation
  const anPanelRef = useRef(null);
  const pendingAnRef = useRef(null);
  const absenderPanelRef = useRef(null);
  const empfaengerPanelRef = useRef(null);
  const datumPanelRef = useRef(null);

  // ── Undo / Redo ──────────────────────────────────────────────
  const [cvUndoStack, setCvUndoStack] = useState([]);
  const [cvRedoStack, setCvRedoStack] = useState([]);
  const [anUndoStack, setAnUndoStack] = useState([]);
  const [anRedoStack, setAnRedoStack] = useState([]);

  // Sobald ein Dokument da ist, gehört ihm der Bildschirm: Es wird an die Oberkante der
  // Insel geschoben, sodass Stellenbeschreibungs-Leiste und Fit-Analyse darüber aus dem
  // Bild wandern — wer sie sehen will, scrollt hoch. Zusammen mit `min-height: 100%` auf
  // `.se-ergebnis` füllen Werkzeugleiste und Blatt dadurch allein das Fenster.
  //
  // Vorher lief das nur beim Wechsel des Bearbeitungsmodus und der erste Lauf wurde
  // übersprungen. Beides ist jetzt falsch: Wer eine fertige Bewerbung öffnet, soll sie
  // sofort groß sehen, nicht erst nach dem Scrollen. Wunsch des Users vom 2026-08-11.
  //
  // useLayoutEffect läuft erst, nachdem die Fit-Analyse oben fertig auf-/zugeklappt ist
  // (synchron im selben Commit), also stimmt die gemessene Position.
  useLayoutEffect(() => {
    // Gescrollt wird die innere Dokument-Insel (.se-main-scroll), NICHT das äußere
    // Mittelfeld (.studio-main) — Letzteres scrollt gar nicht.
    const mainEl = document.querySelector('.se-main-scroll');
    if (!mainEl) return;
    // Die rechte Leiste animiert 0,6s lang und verschiebt dabei das Layout weiter.
    // Darum das Dokument über die gesamte Animationsdauer Frame für Frame oben
    // festhalten, statt nur einmal zu messen (sonst zieht die Animation es wieder weg).
    let raf;
    const start = performance.now();
    const halteOben = () => {
      const dok = mainEl.querySelector('.se-ergebnis');
      if (dok) {
        mainEl.scrollTop += dok.getBoundingClientRect().top - mainEl.getBoundingClientRect().top;
      }
      if (performance.now() - start < 700) raf = requestAnimationFrame(halteOben);
    };
    halteOben();
    return () => cancelAnimationFrame(raf);
  }, [bearbeitungsmodus, status, aktivesDok]);

  // Beim Verlassen des Bearbeitungsmodus offene Bearbeiten-Panels mitschließen
  // (slide-out via requestClose; onClose setzt den State danach auf null).
  const prevBearbModusRef = useRef(bearbeitungsmodus);
  useEffect(() => {
    const war = prevBearbModusRef.current;
    prevBearbModusRef.current = bearbeitungsmodus;
    if (war && !bearbeitungsmodus) {
      pendingSektionRef.current = null;
      pendingAnRef.current = null;
      if (editSektion) sektionPanelRef.current?.requestClose();
      if (editAnItem) anPanelRef.current?.requestClose();
      if (editAbsender) absenderPanelRef.current?.requestClose();
      if (editEmpfaenger) empfaengerPanelRef.current?.requestClose();
      if (editDatum) datumPanelRef.current?.requestClose();
      setSelectedAnBlock(null);
    }
  }, [bearbeitungsmodus, editSektion, editAnItem, editAbsender, editEmpfaenger, editDatum]);

  const stelleRef = useRef(stelle);
  const analyseStrukturRef = useRef(analyseStruktur);
  const confirm = useConfirm();
  const cvRef = useRef(null);
  const anRef = useRef(null);
  const globalEnterRef = useRef({ action: null, disabled: true });
  const pdfInputRef = useRef(null);
  const prevStatusRef = useRef(status);

  useEffect(() => { stelleRef.current = stelle; }, [stelle]);
  useEffect(() => { analyseStrukturRef.current = analyseStruktur; }, [analyseStruktur]);

  // kein_kontingent: Backend meldet kein Guthaben während merged-Task → PricingModal öffnen
  useEffect(() => {
    if (activeJob?.kein_kontingent && onKeinKontingent) onKeinKontingent();
  }, [activeJob?.kein_kontingent]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    if (prevStatus === 'generiert' && status === 'fertig') {
      const strukturKws = (analyseStrukturRef.current?.relevante_keywords || [])
        .map(k => k.name || k.begriffe || (typeof k === 'string' ? k : ''))
        .filter(Boolean);
      const keywords = strukturKws.length > 0
        ? strukturKws
        : parseAnalyse(analyse || stelleRef.current?.analyse || '').keywords;
      setBuildExitKeywords(keywords);
      setBuildExit(true);
      const timer = setTimeout(() => {
        setBuildExit(false);
        setBuildExitKeywords([]);
      }, 520);
      prevStatusRef.current = status;
      return () => clearTimeout(timer);
    }
    prevStatusRef.current = status;
  }, [status, analyse]);

  useEffect(() => {
    if (status !== 'analyse') return;
    if (activeJob?.kind === 'nur_anschreiben') setAktivesDok('an');
    else if (activeJob?.kind === 'nur_cv') setAktivesDok('cv');
  }, [status, activeJob?.kind]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lesbarer Personenname aus dem Profil-Slug: "max_mustermann" -> "Max Mustermann".
  const personName = (str) =>
    (str || '').split(/[_\s-]+/).filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  // Dateinamen: "<Name> Lebenslauf <Firma>.pdf" bzw. "<Name> Anschreiben <Firma>.pdf".
  const pName = stelle ? personName(stelle.profil_name) : '';
  const firmaName = ((stelle?.name || '').split(/\s[–-]\s/)[0] || '')
    .trim().replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  const suffix = firmaName ? ` ${firmaName}` : '';
  const cvName = pName ? `${pName} Lebenslauf${suffix}.pdf` : 'Lebenslauf.pdf';
  const anName = pName ? `${pName} Anschreiben${suffix}.pdf` : 'Anschreiben.pdf';

  const [pdfLaedt2, setPdfLaedt2] = useState({ cv: false, an: false });

  const druckCV = async () => {
    if (!cvRef.current) return;
    setPdfLaedt2((s) => ({ ...s, cv: true }));
    try { await generateAndDownloadPDF(cvRef.current, cvName); }
    finally { setPdfLaedt2((s) => ({ ...s, cv: false })); }
  };

  const druckAN = async () => {
    if (!anRef.current) return;
    setPdfLaedt2((s) => ({ ...s, an: true }));
    try { await generateAndDownloadPDF(anRef.current, anName); }
    finally { setPdfLaedt2((s) => ({ ...s, an: false })); }
  };

  // Fertige PDFs als Datei (Blob-URL) fuer Drag-and-Drop auf den Desktop.
  const cvBlobUrl = async () => {
    if (!cvRef.current) return null;
    const blob = await generateAndDownloadPDF(cvRef.current, cvName, { nurBlob: true });
    return blob ? { url: URL.createObjectURL(blob), name: cvName } : null;
  };
  const anBlobUrl = async () => {
    if (!anRef.current) return null;
    const blob = await generateAndDownloadPDF(anRef.current, anName, { nurBlob: true });
    return blob ? { url: URL.createObjectURL(blob), name: anName } : null;
  };

  useEffect(() => {
    getProfile().then((r) => {
      if (r.data.length > 0) setProfilName((r.data.find(p => p.aktiv) ?? r.data[0]).dateiname);
    });
    getCVVorlagen().then((r) => {
      setCvVorlagen(r.data);
      const def = r.data.find(v => v.dateiname === 'cv_standard') || r.data[0];
      if (def) setCvVorlage(def.dateiname);
    });
    getAnschreibenVorlagen().then((r) => {
      if (r.data.length > 0) setAnVorlage(r.data[0].dateiname);
    });
  }, []);

  useEffect(() => {
    const name = stelle?.profil_name || profilName;
    if (!name) {
      setAktuellesProfil(null);
      return;
    }
    getProfil(name)
      .then((r) => setAktuellesProfil(r.data))
      .catch(() => setAktuellesProfil(null));
  }, [stelle?.profil_name, profilName]);

  // Panel & History zurücksetzen wenn Stelle wechselt
  useEffect(() => {
    pendingSektionRef.current = null;
    pendingAnRef.current = null;
    setEditSektion(null);
    setEditAnItem(null);
    setEditAbsender(false);
    setEditEmpfaenger(false);
    setEditDatum(false);
    setBaseLayerPushed(false);
    setCvUndoStack([]);
    setCvRedoStack([]);
    setAnUndoStack([]);
    setAnRedoStack([]);
    setDismissedPopups(new Set());
  }, [stelle?.id, stelle?.generiert_am]);

  useEffect(() => {
    if (stelle?.generiert_am && !stelle?.bewerbungs_status) {
      const neu = { ...stelle, bewerbungs_status: 'unterlagen_angefertigt' };
      onStelleGeaendert(neu, 'update');
      updateStelle(stelle.id, { bewerbungs_status: 'unterlagen_angefertigt' }).catch(speicherFehler);
    }
  }, [stelle?.id, stelle?.generiert_am]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!statusDropdownOffen) return;
    const handler = (e) => {
      if (statusFabRef.current && !statusFabRef.current.contains(e.target))
        setStatusDropdownOffen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [statusDropdownOffen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Enter') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      if (globalEnterRef.current.disabled) return;
      globalEnterRef.current.action?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── History-Helfer ───────────────────────────────────────────
  const snapCv = () => ({ cv_data: stelle?.cv_data ?? null, cv_rohtext: stelle?.cv_rohtext ?? null });
  // Der Adressblock lebt nicht im Brieftext, sondern in eigenen Feldern. Ohne sie auf dem
  // Merkzettel kann der Pfeil eine Änderung an Absender, Empfänger oder Datum nicht
  // zurückholen — er wüsste gar nicht, wie es vorher aussah.
  const AN_ADRESSFELDER = [
    'absender_name', 'absender_anschrift', 'absender_telefon', 'absender_email',
    'unternehmen', 'job_id', 'empfaenger_strasse', 'empfaenger_plz_ort',
    'empfaenger_ansprechperson', 'brief_datum_anzeige',
  ];
  // Ein noch nie gefülltes Feld wird als leerer Text gesichert, nicht als `null`:
  // Rückgängig schreibt den Merkzettel unverändert zurück, und `null` wäre dort ein
  // anderer Wert als „noch nicht ausgefüllt".
  const snapAn = () => ({
    an_rohtext: stelle?.an_rohtext ?? null,
    an_layout: stelle?.an_layout ?? null,
    ...Object.fromEntries(AN_ADRESSFELDER.map(f => [f, stelle?.[f] ?? ''])),
  });

  const pushCv = () => { setCvUndoStack(s => [...s, snapCv()]); setCvRedoStack([]); };
  const pushAn = () => { setAnUndoStack(s => [...s, snapAn()]); setAnRedoStack([]); };

  // Zustand des Lebenslaufs, bevor die aktuelle Live-Bearbeitung begann (für einen sauberen Undo-Punkt).
  const cvVorEditRef = useRef(null);

  // Live-Bearbeitung: Sektion sofort in die Vorschau spiegeln — ohne Server-Speichern
  // und ohne pro Tastendruck einen Undo-Eintrag anzulegen.
  const onLiveSektion = (cleanSektion) => {
    if (!stelle?.cv_data) return;
    if (!cvVorEditRef.current) cvVorEditRef.current = snapCv(); // Baseline vor der ersten Änderung
    const idx = editSektion?.idx;
    // Live-Vorschau für Profil-Text
    if (idx === -1) {
      onStelleGeaendert({ ...stelle, cv_data: { ...stelle.cv_data, profil_text: cleanSektion.profil_text || '' } }, 'update');
      return;
    }
    const { inhaltRaw, _originalIndex, _sectionKey, _synthetic, ...rest } = cleanSektion;
    const sektionen = [...(stelle.cv_data.sektionen || [])];
    // Nur bestehende Sektionen live spiegeln. Eine noch nicht gespeicherte neue Sektion
    // würde sonst bei jedem Tastendruck erneut angehängt (Duplikate) — die erscheint
    // erst beim Schließen in der Vorschau.
    if (idx == null || idx < 0 || idx >= sektionen.length) return;
    sektionen[idx] = rest;
    onStelleGeaendert({ ...stelle, cv_data: { ...stelle.cv_data, sektionen } }, 'update');
  };

  const onCvUndo = () => {
    if (cvUndoStack.length === 0) return;
    const prev = cvUndoStack[cvUndoStack.length - 1];
    setCvUndoStack(s => s.slice(0, -1));
    setCvRedoStack(s => [...s, snapCv()]);
    const neu = { ...stelle, ...prev };
    onStelleGeaendert(neu, 'update');
    updateStelle(stelle.id, { cv_data: prev.cv_data, cv_rohtext: prev.cv_rohtext }).catch(speicherFehler);
  };

  const onCvRedo = () => {
    if (cvRedoStack.length === 0) return;
    const next = cvRedoStack[cvRedoStack.length - 1];
    setCvRedoStack(s => s.slice(0, -1));
    setCvUndoStack(s => [...s, snapCv()]);
    const neu = { ...stelle, ...next };
    onStelleGeaendert(neu, 'update');
    updateStelle(stelle.id, { cv_data: next.cv_data, cv_rohtext: next.cv_rohtext }).catch(speicherFehler);
  };

  // Vorlage anklicken → Live-Vorschau stylt sofort um (cv_vorlage steuert inferTemplateId)
  const onVorlageChange = (dateiname) => {
    if (!stelle?.id || stelle.cv_vorlage === dateiname) return;
    setCvVorlage(dateiname);
    onStelleGeaendert({ ...stelle, cv_vorlage: dateiname }, 'update');
    updateStelle(stelle.id, { cv_vorlage: dateiname }).catch(speicherFehler);
  };

  // Ganz neuen, leeren Abschnitt anlegen (z.B. "Berufserfahrung", "Projekte").
  // Stationen darin kommen danach über das bestehende Abschnitt-Bearbeiten-Panel dazu.
  // Effizientes Autosave für die dauerhaft aufgeklappten Abschnitte-Panels: nicht bei
  // jedem Tastendruck und nicht per festem Zeit-Intervall speichern, sondern erst 1,2s
  // nach der letzten Änderung. Der zu schreibende Stand wird direkt beim Planen als
  // Schnappschuss gemerkt (nicht erst beim Auslösen aus dem Prop gelesen) — sonst würde
  // ein schneller Wechsel zu einer anderen Stelle den falschen (neuen) Stand speichern.
  const cvAutosaveTimer = useRef(null);
  const cvAutosavePending = useRef(null); // { id, cvData }
  const cvAutosaveJetzt = () => {
    clearTimeout(cvAutosaveTimer.current);
    const pending = cvAutosavePending.current;
    cvAutosavePending.current = null;
    if (!pending) return;
    updateStelle(pending.id, { cv_data: pending.cvData }).catch(speicherFehler);
  };
  // Beim Wechsel zu einer anderen Stelle oder beim Verlassen nichts Ungespeichertes verlieren.
  useEffect(() => () => { if (cvAutosaveTimer.current) cvAutosaveJetzt(); }, [stelle?.id]);

  // Ruhezeit für das Brief-Layout: So lange nach der letzten Änderung wird gewartet,
  // bevor auf den Server geschrieben wird — und so lange gilt eine Änderung noch als
  // Teil derselben Folge.
  const AN_RUHEZEIT_MS = 600;
  const anFolgeRef = useRef({ folge: null, zeit: 0 });

  // Wie beim Lebenslauf weiter oben: Der zu schreibende Stand wird direkt beim Planen
  // gemerkt und nicht erst beim Auslösen aus dem Prop gelesen — sonst schriebe ein
  // schneller Wechsel zu einer anderen Stelle den falschen Stand.
  const anSaveTimer = useRef(null);
  const anSavePending = useRef(null); // { id, anLayout }
  const anSpeichernJetzt = useCallback(() => {
    clearTimeout(anSaveTimer.current);
    anSaveTimer.current = null;
    const pending = anSavePending.current;
    anSavePending.current = null;
    if (!pending) return;
    // Geschrieben wird der frischeste Stand, nicht der gemerkte: Andere Wege ändern
    // dasselbe Feld sofort — eine Textbearbeitung etwa —, und ein wartender älterer
    // Schnappschuss würde diese Änderung wieder wegwischen. Der gemerkte Stand dient
    // nur als Rückfallebene, falls inzwischen die Stelle gewechselt wurde.
    const frisch = stelleRef.current?.id === pending.id ? stelleRef.current.an_layout : null;
    updateStelle(pending.id, { an_layout: frisch ?? pending.anLayout }).catch(speicherFehler);
  }, [speicherFehler]);
  const anSpeichernPlanen = (id, anLayout) => {
    anSavePending.current = { id, anLayout };
    clearTimeout(anSaveTimer.current);
    anSaveTimer.current = setTimeout(anSpeichernJetzt, AN_RUHEZEIT_MS);
  };
  // Rückgängig schreibt selbst und macht genau die Änderung ungeschehen, die noch
  // ungespeichert wartet. Die darf danach nicht mehr losgehen, sonst überschriebe sie
  // das Zurückgenommene wieder.
  const anSpeichernVerwerfen = () => {
    clearTimeout(anSaveTimer.current);
    anSaveTimer.current = null;
    anSavePending.current = null;
    anFolgeRef.current = { folge: null, zeit: 0 };
  };
  // Beim Verlassen des Bearbeitungsmodus, beim Wechsel der Stelle und beim Schließen
  // darf nichts Ungespeichertes zurückbleiben.
  useEffect(() => { if (!bearbeitungsmodus) anSpeichernJetzt(); }, [bearbeitungsmodus, anSpeichernJetzt]);
  useEffect(() => () => anSpeichernJetzt(), [stelle?.id, anSpeichernJetzt]);

  const onAnUndo = () => {
    if (anUndoStack.length === 0) return;
    anSpeichernVerwerfen();
    const prev = anUndoStack[anUndoStack.length - 1];
    setAnUndoStack(s => s.slice(0, -1));
    setAnRedoStack(s => [...s, snapAn()]);
    onStelleGeaendert({ ...stelle, ...prev }, 'update');
    updateStelle(stelle.id, prev).catch(speicherFehler);
  };

  const onAnRedo = () => {
    if (anRedoStack.length === 0) return;
    anSpeichernVerwerfen();
    const next = anRedoStack[anRedoStack.length - 1];
    setAnRedoStack(s => s.slice(0, -1));
    setAnUndoStack(s => [...s, snapAn()]);
    onStelleGeaendert({ ...stelle, ...next }, 'update');
    updateStelle(stelle.id, next).catch(speicherFehler);
  };

  const onEditToggle = (idx, section) => {
    if (editSektion?.idx === idx) {
      // Selbe Sektion nochmal: Panel schließen
      sektionPanelRef.current?.requestClose();
    } else if (editSektion) {
      // Andere Sektion während Panel offen: erst altes Panel schließen, dann neues öffnen
      pendingSektionRef.current = { idx, section };
      sektionPanelRef.current?.requestClose();
    } else {
      // Kein Panel offen: direkt öffnen
      if (editAnItem) { anPanelRef.current?.requestClose(); pendingAnRef.current = null; setEditAnItem(null); }
      if (editAbsender) { absenderPanelRef.current?.requestClose(); setEditAbsender(false); }
      if (editEmpfaenger) { empfaengerPanelRef.current?.requestClose(); setEditEmpfaenger(false); }
      setEditSektion({ idx, section });
      // Basis-Insel NICHT wegschieben — der Bearbeiten-Bereich sitzt jetzt fest darin
    }
  };

  const onPanelClosingStart = () => {
    // Basis-Layer nur zurückschieben wenn kein Wechsel (d.h. Panel bleibt offen für nächste Sektion)
    if (!pendingSektionRef.current) {
      setBaseLayerPushed(false);
    }
  };

  const onPanelClose = () => {
    const pending = pendingSektionRef.current;
    pendingSektionRef.current = null;
    if (pending) {
      // Neues Panel mit neuer Sektion öffnen (Basis-Layer bleibt gepusht)
      setEditSektion(pending);
    } else {
      setEditSektion(null);
    }
  };

  // ── Anschreiben Panel ────────────────────────────────────────
  const onAnEditToggle = (item, profil, anRohtext) => {
    if (editAnItem?.item?.rohzeile === item.rohzeile) {
      anPanelRef.current?.requestClose();
    } else if (editAnItem) {
      pendingAnRef.current = { item, profil, anRohtext };
      anPanelRef.current?.requestClose();
    } else {
      if (editSektion) { sektionPanelRef.current?.requestClose(); pendingSektionRef.current = null; setEditSektion(null); }
      if (editAbsender) { absenderPanelRef.current?.requestClose(); setEditAbsender(false); }
      if (editEmpfaenger) { empfaengerPanelRef.current?.requestClose(); setEditEmpfaenger(false); }
      setEditAnItem({ item, profil, anRohtext });
      setBaseLayerPushed(true);
    }
  };

  const onAnPanelClosingStart = () => {
    if (!pendingAnRef.current) setBaseLayerPushed(false);
  };

  const onAnPanelClose = () => {
    const pending = pendingAnRef.current;
    pendingAnRef.current = null;
    if (pending) {
      setEditAnItem(pending);
    } else {
      setEditAnItem(null);
    }
  };

  const onSaveAnEdit = (rohzeile, neuerText, htmlPayload = null) => {
    if (!stelle?.an_rohtext) return;
    const idx = stelle.an_rohtext.indexOf(rohzeile);
    if (idx === -1) return;
    pushAn();
    const neuerRohtext =
      stelle.an_rohtext.slice(0, idx) + neuerText + stelle.an_rohtext.slice(idx + rohzeile.length);
    let neuesLayout = stelle.an_layout || {};
    if (htmlPayload !== null && htmlPayload.itemKey) {
      const blockHtml = { ...(stelle.an_layout?.block_html || {}) };
      if (htmlPayload.html) {
        blockHtml[htmlPayload.itemKey] = { html: htmlPayload.html, text: htmlPayload.text };
      } else {
        delete blockHtml[htmlPayload.itemKey];
      }
      neuesLayout = { ...neuesLayout, block_html: blockHtml };
    }
    onStelleGeaendert({ ...stelle, an_rohtext: neuerRohtext, an_layout: neuesLayout }, 'update');
    updateStelle(stelle.id, { an_rohtext: neuerRohtext, an_layout: neuesLayout }).catch(speicherFehler);
  };

  const applyBlockHtml = (layout, htmlPayload) => {
    if (!htmlPayload?.itemKey) return layout;
    const blockHtml = { ...(layout?.block_html || {}) };
    if (htmlPayload.html) { blockHtml[htmlPayload.itemKey] = { html: htmlPayload.html, text: htmlPayload.text }; }
    else { delete blockHtml[htmlPayload.itemKey]; }
    return { ...layout, block_html: blockHtml };
  };

  const onAbsenderInlineChange = (newText, htmlPayload = null) => {
    if (!stelle?.id) return;
    const zeilen = newText.split('\n').map(z => z.trim()).filter(Boolean);
    const felder = {};
    if (zeilen.length === 0) return;
    const letzteIdx = zeilen.length - 1;
    if (zeilen[letzteIdx].includes('@')) { felder.absender_email = zeilen[letzteIdx]; zeilen.splice(letzteIdx, 1); }
    else felder.absender_email = '';
    if (zeilen.length > 1 && /^[+\d(]/.test(zeilen[zeilen.length - 1])) felder.absender_telefon = zeilen.pop();
    else felder.absender_telefon = '';
    felder.absender_name = zeilen.shift() || '';
    felder.absender_anschrift = zeilen.join('\n');
    const neuesLayout = applyBlockHtml(stelle.an_layout, htmlPayload);
    // Erst merken, dann ändern — wie beim Brieftext. `InlineText` meldet sich nur bei
    // einer echten Änderung, es entstehen also keine leeren Schritte.
    pushAn();
    onStelleGeaendert({ ...stelle, ...felder, an_layout: neuesLayout }, 'update');
    updateStelle(stelle.id, { ...felder, an_layout: neuesLayout }).catch(speicherFehler);
  };

  const onEmpfaengerInlineChange = (newText, htmlPayload = null) => {
    if (!stelle?.id) return;
    const zeilen = newText.split('\n').map(z => z.trim()).filter(Boolean);
    // Untergrenze wie beim Absender: Aus nichts wird nie gespeichert. Ohne diese Sperre
    // schriebe ein leer gelesener Block Firma, Anschrift und Ansprechperson auf einen
    // Schlag leer — dieselbe Fehlerklasse wie der Vorfall vom 2026-08-09.
    if (zeilen.length === 0) return;
    const felder = {};
    felder.unternehmen = zeilen.shift() || '';
    const jobIdIdx = zeilen.findIndex(z => /^job-?id:/i.test(z));
    if (jobIdIdx !== -1) { felder.job_id = zeilen.splice(jobIdIdx, 1)[0].replace(/^job-?id:\s*/i, ''); } else felder.job_id = '';
    const plzIdx = zeilen.findIndex(z => /^\d{4,5}\s/.test(z));
    if (plzIdx !== -1) { felder.empfaenger_plz_ort = zeilen.splice(plzIdx, 1)[0]; } else felder.empfaenger_plz_ort = '';
    const strasseIdx = zeilen.findIndex(z => /\d/.test(z));
    if (strasseIdx !== -1) { felder.empfaenger_strasse = zeilen.splice(strasseIdx, 1)[0]; } else felder.empfaenger_strasse = '';
    felder.empfaenger_ansprechperson = zeilen.join(' ');
    const neuesLayout = applyBlockHtml(stelle.an_layout, htmlPayload);
    pushAn(); // siehe `onAbsenderInlineChange`
    onStelleGeaendert({ ...stelle, ...felder, an_layout: neuesLayout }, 'update');
    updateStelle(stelle.id, { ...felder, an_layout: neuesLayout }).catch(speicherFehler);
  };

  const onDatumInlineChange = (newText, htmlPayload = null) => {
    if (!stelle?.id) return;
    if (!newText.trim()) return; // Untergrenze, siehe `onEmpfaengerInlineChange`.
    const felder = { brief_datum_anzeige: newText };
    const neuesLayout = applyBlockHtml(stelle.an_layout, htmlPayload);
    pushAn(); // siehe `onAbsenderInlineChange`
    onStelleGeaendert({ ...stelle, ...felder, an_layout: neuesLayout }, 'update');
    updateStelle(stelle.id, { ...felder, an_layout: neuesLayout }).catch(speicherFehler);
  };

  const onAnLayoutChange = (patch) => {
    // Eine zusammenhängende Folge — Pfeiltasten gedrückt halten, am Farbregler ziehen —
    // soll einen einzigen Rückgängig-Schritt ergeben, nicht zwanzig. Solche Änderungen
    // tragen eine Kennung mit; kommt zweimal dieselbe kurz hintereinander, gehört sie
    // zur selben Folge. Ziehen und Größenändern tragen keine Kennung und sind damit
    // immer ihr eigener Schritt — zwei schnelle Züge dürfen nicht verschmelzen.
    // Eine technische Umrechnung — etwa das Verteilen alter Briefe auf echte Seiten —
    // ist keine Bearbeitung des Users und bekommt keinen Rückgängig-Schritt. Sonst
    // nähme der erste Klick auf Rückgängig sie zurück, und der Brief stünde wieder in
    // Koordinaten, die die Anzeige nicht mehr versteht.
    if (!patch.ohneRueckgaengig) {
      const jetzt = Date.now();
      if (!gehoertZurFolge(anFolgeRef.current, patch.folge, jetzt, AN_RUHEZEIT_MS)) pushAn();
      anFolgeRef.current = { folge: patch.folge ?? null, zeit: jetzt };
    }

    const anLayout = { ...(stelle.an_layout || {}) };
    if (patch.offsets !== undefined) anLayout.offsets = patch.offsets;
    if (patch.block_styles !== undefined) {
      anLayout.block_styles = { ...(stelle.an_layout?.block_styles || {}), ...patch.block_styles };
    }
    // Frei angelegte Textfelder: `eigene` ist die Liste ihrer Kennungen, ihr Inhalt
    // liegt im selben Fach wie der der festen Blöcke.
    if (patch.eigene !== undefined) anLayout.eigene = patch.eigene;
    // Wie viele Blätter der Brief hat. Steht eigens hier und wird nicht mehr aus den
    // Blöcken abgeleitet — sonst stürbe eine Seite mit ihrem letzten Textfeld.
    if (patch.seiten !== undefined) anLayout.seiten = patch.seiten;
    if (patch.block_html !== undefined) {
      anLayout.block_html = { ...(stelle.an_layout?.block_html || {}), ...patch.block_html };
    }
    // Auf dem Bildschirm sofort, auf dem Server gebündelt.
    onStelleGeaendert({ ...stelle, an_layout: anLayout }, 'update');
    anSpeichernPlanen(stelle.id, anLayout);
  };

  // Die automatische Kürzung ist am 2026-08-09 auf Wunsch des Users entfernt worden.
  //
  // Sie feuerte, sobald der Brief auf eine zweite Seite lief, schickte den GANZEN
  // Brieftext ungefragt an die KI und ersetzte ihn durch deren Antwort. Wer einen Absatz
  // ergänzte, verlor damit seinen Brief — beim Tippen, ohne Vorwarnung, ohne Rückfrage.
  // Der Undo-Eintrag rettete nur den Rohtext, nicht die eigenen Formatierungen.
  //
  // Nicht wieder einbauen. Der User will das Überlaufen anders lösen und entscheidet
  // selbst, was mit einem zu langen Brief geschieht.

  // ── Absender / Empfänger Panel ───────────────────────────────
  const onAbsenderToggle = () => {
    if (editAbsender) {
      absenderPanelRef.current?.requestClose();
    } else {
      if (editAnItem) { anPanelRef.current?.requestClose(); pendingAnRef.current = null; setEditAnItem(null); }
      if (editSektion) { sektionPanelRef.current?.requestClose(); pendingSektionRef.current = null; setEditSektion(null); }
      if (editEmpfaenger) { empfaengerPanelRef.current?.requestClose(); setEditEmpfaenger(false); }
      if (editDatum) { datumPanelRef.current?.requestClose(); setEditDatum(false); }
      setEditAbsender(true);
      setBaseLayerPushed(true);
    }
  };

  const onAbsenderPanelClose = () => {
    setEditAbsender(false);
  };

  const onAbsenderPanelClosingStart = () => {
    setBaseLayerPushed(false);
  };

  const onSaveAbsender = (felder) => {
    const neu = { ...stelle, ...felder };
    onStelleGeaendert(neu, 'update');
    updateStelle(stelle.id, felder).catch(speicherFehler);
  };

  const onEmpfaengerToggle = () => {
    if (editEmpfaenger) {
      empfaengerPanelRef.current?.requestClose();
    } else {
      if (editAnItem) { anPanelRef.current?.requestClose(); pendingAnRef.current = null; setEditAnItem(null); }
      if (editSektion) { sektionPanelRef.current?.requestClose(); pendingSektionRef.current = null; setEditSektion(null); }
      if (editAbsender) { absenderPanelRef.current?.requestClose(); setEditAbsender(false); }
      if (editDatum) { datumPanelRef.current?.requestClose(); setEditDatum(false); }
      setEditEmpfaenger(true);
      setBaseLayerPushed(true);
    }
  };

  const onEmpfaengerPanelClose = () => {
    setEditEmpfaenger(false);
  };

  const onEmpfaengerPanelClosingStart = () => {
    setBaseLayerPushed(false);
  };

  const onSaveEmpfaenger = (felder) => {
    const neu = { ...stelle, ...felder };
    onStelleGeaendert(neu, 'update');
    updateStelle(stelle.id, felder).catch(speicherFehler);
  };

  const onDatumToggle = () => {
    if (editDatum) {
      datumPanelRef.current?.requestClose();
    } else {
      if (editAnItem) { anPanelRef.current?.requestClose(); pendingAnRef.current = null; setEditAnItem(null); }
      if (editSektion) { sektionPanelRef.current?.requestClose(); pendingSektionRef.current = null; setEditSektion(null); }
      if (editAbsender) { absenderPanelRef.current?.requestClose(); setEditAbsender(false); }
      if (editEmpfaenger) { empfaengerPanelRef.current?.requestClose(); setEditEmpfaenger(false); }
      setEditDatum(true);
      setBaseLayerPushed(true);
    }
  };

  const onDatumPanelClose = () => { setEditDatum(false); };
  const onDatumPanelClosingStart = () => { setBaseLayerPushed(false); };

  const onSaveDatum = (felder) => {
    const neu = { ...stelle, ...felder };
    onStelleGeaendert(neu, 'update');
    updateStelle(stelle.id, felder).catch(speicherFehler);
  };

  const sektionToRaw = (s) => {
    const lines = [];
    if (s.typ === 'eintraege') {
      for (const e of (s.eintraege || [])) {
        if (e.datum)       lines.push(`DATUM: ${e.datum}`);
        if (e.jobtitel)    lines.push(`JOBTITEL: ${e.jobtitel}`);
        if (e.arbeitgeber) lines.push(`ARBEITGEBER: ${e.arbeitgeber}`);
        if (e.firma)       lines.push(`FIRMA: ${e.firma}`);
        for (const b of (e.bullets || [])) lines.push(`BULLET: ${b}`);
      }
    } else if (s.typ === 'kenntnisse') {
      for (const k of (s.kenntnisse || [])) {
        if (k.kategorie) lines.push(`KKATEGORIE: ${k.kategorie}`);
        lines.push(`KTITEL: ${k.titel}`);
        lines.push(`KBESCHR: ${k.beschreibung}`);
      }
    } else {
      for (const b of (s.bullets || [])) lines.push(`BULLET: ${b}`);
    }
    return lines.join('\n');
  };

  const onSaveEdit = async (neueSektion) => {
    // Profil-Text (Pseudo-Sektion idx=-1)
    if (editSektion?.idx === -1) {
      const newProfilText = neueSektion.profil_text || '';
      if (cvVorEditRef.current) {
        setCvUndoStack(s => [...s, cvVorEditRef.current]);
        setCvRedoStack([]);
        cvVorEditRef.current = null;
      } else {
        pushCv();
      }
      if (stelle?.cv_data) {
        const neuesCvData = { ...stelle.cv_data, profil_text: newProfilText };
        onStelleGeaendert({ ...stelle, cv_data: neuesCvData }, 'update');
        updateStelle(stelle.id, { cv_data: neuesCvData }).catch(speicherFehler);
      } else if (stelle?.cv_rohtext) {
        const bodyStart = stelle.cv_rohtext.indexOf('\n## ');
        const body = bodyStart >= 0 ? stelle.cv_rohtext.slice(bodyStart) : '';
        const neuerRohtext = newProfilText + body;
        onStelleGeaendert({ ...stelle, cv_rohtext: neuerRohtext }, 'update');
        updateStelle(stelle.id, { cv_rohtext: neuerRohtext }).catch(speicherFehler);
      }
      return;
    }
    if (stelle?.cv_data) {
      // Undo-Punkt = Zustand vor der Live-Bearbeitung (falls vorhanden), sonst der aktuelle.
      if (cvVorEditRef.current) {
        setCvUndoStack(s => [...s, cvVorEditRef.current]);
        setCvRedoStack([]);
        cvVorEditRef.current = null;
      } else {
        pushCv();
      }
      const bereinigteSektion = (() => {
        const { inhaltRaw, _originalIndex, _sectionKey, _synthetic, ...rest } = neueSektion;
        return rest;
      })();
      const sektionen = [...(stelle.cv_data.sektionen || [])];
      if (editSektion.idx >= 0 && editSektion.idx < sektionen.length) {
        sektionen[editSektion.idx] = bereinigteSektion;
      } else {
        sektionen.push(bereinigteSektion);
      }
      const neuesCvData = {
        ...stelle.cv_data,
        sektionen,
      };
      onStelleGeaendert({ ...stelle, cv_data: neuesCvData }, 'update');
      updateStelle(stelle.id, { cv_data: neuesCvData }).catch(speicherFehler);
    } else if (stelle?.cv_rohtext) {
      pushCv();
      const rohSektionen = stelle.cv_rohtext
        .split(/\n?## /).slice(1)
        .map((teil, i) => {
          const zeilen = teil.split('\n');
          const name = zeilen[0].trim();
          const inhaltRaw = zeilen.slice(1).join('\n').trim();
          return i === editSektion.idx
            ? `## ${neueSektion.name || name}\n${sektionToRaw(neueSektion)}`
            : `## ${name}\n${inhaltRaw}`;
        });
      const headerPart = stelle.cv_rohtext.split('\n## ')[0].trim();
      const neuerRohtext = headerPart ? `${headerPart}\n${rohSektionen.join('\n')}` : rohSektionen.join('\n');
      onStelleGeaendert({ ...stelle, cv_rohtext: neuerRohtext }, 'update');
      updateStelle(stelle.id, { cv_rohtext: neuerRohtext }).catch(speicherFehler);
    }
  };

  useEffect(() => {
    if (!stelle) return;

    setText(stelle.text || '');
    setUrl(stelle.url || '');
    {
      const hatUrl = !!stelle.url;
      const hatText = !!String(stelle.text || '').trim();
      if (stelle._urlFehler) {
        setFehler(stelle._fehlerMsg || 'URL konnte nicht gelesen werden.');
        setModus('text');
        setShowUrlInput(false);
      } else {
        setFehler(null);
        setModus(hatUrl || !hatText ? 'url' : 'text');
        setShowUrlInput(hatUrl || !hatText);
        setTextGeladen(hatUrl && hatText);
      }
    }

    // Laufenden Job in globalen Tracker eintragen (falls noch nicht dort)
    if (stelle._runningJobId && !stelle.generiert_am && stelle._runningStatus !== 'warten') {
      if (!activeJob) {
        registerJob(stelle._runningJobId, stelle.id, stelle._runningStatus === 'generiert' ? 'generierung' : 'analyse');
      }
    }

    if (stelle.generiert_am && !stelle.analyse) {
      getStellen().then((r) => {
        const frisch = r.data.find((s) => s.id === stelle.id);
        if (frisch?.analyse) onStelleGeaendert(frisch, 'update');
      });
    }
  }, [stelle?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Job-Seiteneffekte: Analyse persistieren, Jobtitel updaten, Fertig-Flow
  useEffect(() => {
    if (!activeJob || !stelle) return;

    // Jobtitel vom Backend übernehmen
    if (activeJob.jobtitel && stelleRef.current?.name !== activeJob.jobtitel) {
      onStelleGeaendert({ ...stelleRef.current, name: activeJob.jobtitel }, 'update');
    }

    // Analyse angekommen → in Stelle persistieren
    if (activeJob.status === 'warten' && activeJob.analyse && !stelleRef.current?.analyse) {
      onStelleGeaendert({ ...stelleRef.current, analyse: activeJob.analyse, analyse_struktur: activeJob.analyse_struktur, _runningStatus: 'warten' }, 'update');
      updateStelle(stelleRef.current.id, { analyse: activeJob.analyse }).catch(speicherFehler);
    }

    // Generierung fertig → frische Stelle laden, dann erst Job entfernen
    if (activeJob.status === 'fertig') {
      capture('job_generation_completed', { source: modus === 'url' ? 'job_url' : 'manual_input', success: true });
      const jobId = activeJob.jobId;
      getStellen().then((r) => {
        const frisch = r.data.find((x) => x.id === stelleRef.current?.id);
        if (frisch) onStelleGeaendert({ ...frisch, _runningJobId: null, _runningStatus: null }, 'update');
        removeJob(jobId);
      });
    }

    // Fehler → anzeigen + Job aufräumen
    if (activeJob.status === 'fehler') {
      const msg = activeJob.fehler || 'Generierung fehlgeschlagen. Bitte erneut versuchen.';
      const istUrlFehler = msg.includes('URL');
      setFehler(msg);
      if (istUrlFehler) { setShowUrlInput(false); setModus('text'); }
      onStelleGeaendert({ ...stelleRef.current, _runningJobId: null, _runningStatus: null, ...(istUrlFehler ? { _urlFehler: true, _fehlerMsg: msg } : {}) }, 'update');
      removeJob(activeJob.jobId);
    }
  }, [activeJob?.status, activeJob?.jobtitel, activeJob?.analyse]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch Stellentext wenn URL vorhanden aber kein Text gespeichert
  useEffect(() => {
    if (!stelle?.id || !stelle?.url || stelle?.text || stelle?._urlFehler) return;
    urlFetchInFlightRef.current = true;
    setTextLaedt(true);
    fetchStelleText(stelle.id).then(res => {
      if (res.data.ok && res.data.text) {
        setText(res.data.text);
        setTextGeladen(true);
        const updates = { text: res.data.text };
        if (stelle.name === 'Neue Stellenanzeige') {
          const titel = extractTitelAusText(res.data.text);
          if (titel) { updates.name = titel; updateStelle(stelle.id, { name: titel }).catch(speicherFehler); }
        }
        onStelleGeaendert({ ...stelle, ...updates }, 'update');
      }
    }).catch(() => {}).finally(() => {
      urlFetchInFlightRef.current = false;
      setTextLaedt(false);
    });
  }, [stelle?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync backend-loaded text/url into local state (e.g. when URL fetch completes server-side)
  useEffect(() => {
    if (!stelle) return;
    if (!text && stelle.text) { setText(stelle.text); setTextGeladen(true); }
    if (!url && stelle.url) setUrl(stelle.url);
  }, [stelle?.text, stelle?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save text/url to parent state while typing so data survives navigation
  useEffect(() => {
    const cur = stelleRef.current;
    if (!cur) return;
    if (status !== null || cur._runningJobId) return;
    if (!text && cur.text) return; // don't overwrite backend-loaded text with empty local state
    if (text === (cur.text || '') && url === (cur.url || '')) return;
    const timer = setTimeout(() => {
      const updates = { text, url };
      if (cur.name === 'Neue Stellenanzeige' && text.trim()) {
        const titel = extractTitelAusText(text);
        if (titel) updates.name = titel;
      }
      onStelleGeaendert({ ...cur, ...updates }, 'update');
      updateStelle(cur.id, updates).catch(speicherFehler);
    }, 800);
    return () => clearTimeout(timer);
  }, [text, url, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSpeichern = async () => {
    if (!stelle) return;
    await updateStelle(stelle.id, { text, url });
    onStelleGeaendert({ ...stelle, text, url }, 'update');
  };

  const onStellenPdfUpload = async (e) => {
    const datei = e.target.files[0];
    if (!datei) return;
    setPdfLaedt(true);
    setFehler(null);
    try {
      const res = await extractStellenPdf(datei);
      setText(res.data.text || '');
      setModus('text');
      setShowUrlInput(false);
    } catch {
      setFehler('PDF konnte nicht gelesen werden.');
    } finally {
      setPdfLaedt(false);
      e.target.value = '';
    }
  };

  const onAnalyseStarten = async (genModus = 'alles') => {
    if (!stelle) return;
    if (urlFetchInFlightRef.current) return;
    if (typeof genModus !== 'string') genModus = 'alles';
    setFehler(null);
    await onSpeichern();
    // Anschreiben-Layout zurücksetzen damit Blöcke nach der Generierung an natürlicher Position landen
    if (genModus !== 'nur_cv' && stelle.an_layout && (stelle.an_layout.offsets || stelle.an_layout.block_html)) {
      // Nur fragen, wenn wirklich eigene Arbeit verloren geht.
      const hatEigenePositionen = Object.values(stelle.an_layout.offsets || {}).some(o => o?.dragged);
      const hatEigenenText = Object.keys(stelle.an_layout.block_html || {}).length > 0;
      if (hatEigenePositionen || hatEigenenText) {
        const weiter = await confirm(
          'Der neue Brief wird in der Grundaufstellung angeordnet. Ihre eigenen Positionen und Textformatierungen gehen dabei verloren.'
        );
        if (!weiter) return;
      }
      const clearedLayout = { ...stelle.an_layout, offsets: {}, block_html: {} };
      onStelleGeaendert({ ...stelleRef.current, an_layout: clearedLayout }, 'update');
      updateStelle(stelle.id, { an_layout: clearedLayout }).catch(speicherFehler);
    }
    const nutzeGeladenenUrlText =
      modus === 'url' && textGeladen && Boolean(text.trim());
    try {
      const res = await starteAnalyse({
        profil_name: profilName,
        stellen_text:
          modus === 'text' || nutzeGeladenenUrlText ? text : undefined,
        stellen_url:
          modus === 'url' && !nutzeGeladenenUrlText ? url : undefined,
        stelle_id: stelle.id,
        cv_vorlage: cvVorlage,
        anschreiben_vorlage: anVorlage,
        tonlage: tonlage || 'klassisch',
        modus: genModus,
      });
      const id = res.data.job_id;
      capture('job_generation_started', { source: modus === 'url' ? 'job_url' : 'manual_input' });
      registerJob(id, stelle.id, genModus === 'alles' ? 'komplett' : genModus);
      onStelleGeaendert({ ...stelleRef.current, text, url, _runningJobId: id, _runningStatus: 'analyse' }, 'update');
    } catch (err) {
      setFehler(err?.response?.data?.detail || 'Analyse fehlgeschlagen.');
    }
  };

  const onWeiterGenerieren = async () => {
    if (status === 'generiert') return; // schon am Laufen
    if (!jobId) {
      onAnalyseStarten();
      return;
    }
    onStelleGeaendert({ ...stelleRef.current, _runningStatus: 'generiert' }, 'update');
    try {
      await starteGenerierung({
        job_id: jobId,
        stelle_id: stelleRef.current?.id,
        profil_name: profilName,
        cv_vorlage: cvVorlage,
        anschreiben_vorlage: anVorlage,
        tonlage: tonlage || 'klassisch',
      });
      registerJob(jobId, stelle.id, 'generierung');
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      if (err?.response?.status === 402) {
        onStelleGeaendert({ ...stelleRef.current, _runningStatus: 'warten' }, 'update');
        onKeinKontingent?.();
      } else {
        setFehler(detail || 'Generierung fehlgeschlagen.');
        onStelleGeaendert({ ...stelleRef.current, _runningStatus: 'warten' }, 'update');
      }
    }
  };

  const onAbbrechen = () => {
    if (jobId) removeJob(jobId);
    if (stelle) {
      jobAbbrechen(stelle.id).catch(() => {});
      onStelleGeaendert({ ...stelleRef.current, _runningJobId: null, _runningStatus: null }, 'update');
    }
  };


  const kannGenerieren = !!profilName && !!cvVorlage && !!anVorlage && !!(modus === 'text' ? text : url);

  const autoAnalyseFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoAnalyse) { autoAnalyseFiredRef.current = false; return; }
    if (
      kannGenerieren &&
      !textLaedt &&
      !urlFetchInFlightRef.current &&
      !autoAnalyseFiredRef.current
    ) {
      autoAnalyseFiredRef.current = true;
      onAnalyseStarten();
      onAutoAnalyseDone?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAnalyse, kannGenerieren, textGeladen, textLaedt]);

  if (!stelle) {
    return (
      <div className="se-editor se-leer">
        <p>Wähle links eine Stellenanzeige aus oder lege eine neue an.</p>
      </div>
    );
  }

  const istAbgeschickt = status === null && !!stelle?.generiert_am;
  const bautBewerbung = status === 'generiert' || (activeJob?.kind === 'komplett' && status === 'analyse');
  const bautNurAnschreiben = status === 'analyse' && activeJob?.kind === 'nur_anschreiben';
  const bautNurCV = status === 'analyse' && activeJob?.kind === 'nur_cv';
  // Lebenslauf-Entwurf (Quick-Create-Button): eigenständiger Lebenslauf ohne
  // Stellenanzeige — nie das Link/Text/PDF-Formular oder den Anschreiben-Tab zeigen,
  // unabhängig davon, ob schon Abschnitte befüllt wurden.
  const istLebenslaufEntwurf = stelle?.quelle === 'lebenslauf';
  // Zusätzlich: noch komplett ohne Inhalt → CVVorschau zeigt nur den Header.
  const istLeererLebenslaufEntwurf = istLebenslaufEntwurf
    && !stelle?.cv_data && !stelle?.cv_rohtext && !stelle?.url && !stelle?.text && !stelle?.generiert_am;
  const zeigeFormGrid = !istLebenslaufEntwurf && !istAbgeschickt && !bautBewerbung && !bautNurAnschreiben && !bautNurCV && status !== 'fertig';
  const zeigeStatus = status === 'analyse' || status === 'warten' || status === 'generiert' || status === 'fertig';
  const analyseKeywords = parseAnalyse(analyse || stelle?.analyse || '').keywords;

  const dokTabs = (
    <div className="se-dok-tabs">
      <button
        className={`se-dok-tab${aktivesDok === 'cv' ? ' aktiv' : ''}`}
        onClick={() => setAktivesDok('cv')}
      >Lebenslauf</button>
      {!istLebenslaufEntwurf && (
        <button
          className={`se-dok-tab${aktivesDok === 'an' ? ' aktiv' : ''}`}
          onClick={() => setAktivesDok('an')}
        >
          Anschreiben
          {stelle?.qualitaet_warnung?.typ === 'anschreiben_substanz' && (
            <span className="se-qualitaet-dot" title="Anschreiben prüfen" />
          )}
        </button>
      )}
    </div>
  );

  const btnLaedt = textLaedt || status === 'analyse' || status === 'generiert';
  const btnLabel = btnLaedt
    ? (textLaedt ? 'Stellenanzeige wird gelesen…' : status === 'analyse' ? 'Analyse läuft…' : 'Wird generiert…')
    : status === 'warten' ? 'Bewerbung generieren'
    : 'Analyse Starten';
  const btnDisabled = btnLaedt || (status === null && !kannGenerieren);
  const btnAction = (status === 'warten' && jobId) ? onWeiterGenerieren : onAnalyseStarten;
  globalEnterRef.current = { action: btnAction, disabled: btnDisabled };

  const bewStatus = stelle?.bewerbungs_status;
  const aktiverStatusOpt = STATUS_OPTIONEN.find(o => o.key === bewStatus) ?? null;

  const onBewStatusAendern = (neuerStatus) => {
    const neu = { ...stelle, bewerbungs_status: neuerStatus };
    onStelleGeaendert(neu, 'update');
    updateStelle(stelle.id, { bewerbungs_status: neuerStatus }).catch(speicherFehler);
    setStampAnzeige(neuerStatus);
    setStatusDropdownOffen(false);
    setTimeout(() => setStampAnzeige(null), 1800);
  };

  // Geführter Schritt aus der offenen Mappe: nach echtem E-Mail-Abschicken als
  // "abgeschickt" markieren — wie der Dashboard-Knopf setzt das auch das Datum.
  const onAlsAbgeschickt = () => {
    const heute = new Date().toISOString().slice(0, 10);
    const neu = { ...stelle, bewerbungs_status: 'abgeschickt', abschick_datum: stelle.abschick_datum || heute };
    onStelleGeaendert(neu, 'update');
    updateStelle(stelle.id, { bewerbungs_status: 'abgeschickt', abschick_datum: neu.abschick_datum }).catch(speicherFehler);
    setStampAnzeige('abgeschickt');
    setTimeout(() => setStampAnzeige(null), 1800);
  };

  const dokumentOptionen = stelle?.dokument_optionen || {};
  const stellenTextLaenge = (text || '').trim().length;
  // eslint-disable-next-line no-unused-vars
  const stellenQuelleLabel = url ? 'URL & Text' : 'Textquelle';
  // eslint-disable-next-line no-unused-vars
  const stellenUmfangLabel = stellenTextLaenge
    ? `${stellenTextLaenge.toLocaleString('de-DE')} Zeichen`
    : 'Keine Beschreibung';

  const quelleAlsUrl = () => {
    setTextGeladen(false);
    setShowUrlInput(true);
    setModus('url');
  };

  const quelleAlsText = () => {
    setTextGeladen(false);
    setShowUrlInput(false);
    setModus('text');
  };

  const onInseratNachladen = async () => {
    setTextLaedt(true);
    try {
      const res = await fetchStelleText(stelle.id);
      if (res.data.ok && res.data.text) {
        setText(res.data.text);
        setTextGeladen(true);
        const updates = { text: res.data.text };
        if (stelle.name === 'Neue Stellenanzeige') {
          const titel = extractTitelAusText(res.data.text);
          if (titel) { updates.name = titel; updateStelle(stelle.id, { name: titel }).catch(speicherFehler); }
        }
        onStelleGeaendert({ ...stelle, ...updates }, 'update');
      }
    } catch {}
    setTextLaedt(false);
  };

  // KI-Panel immer sichtbar wenn Bearbeitungsmodus; Inhalt richtet sich nach selektiertem Block
  const inlineAnItem = (() => {
    if (!bearbeitungsmodus || !stelle?.an_rohtext || aktivesDok !== 'an') return null;
    const { betreff, betreffRohzeile, paragraphen } = parseAnschreiben(stelle.an_rohtext);
    if (selectedAnBlock === 'betreff' && betreff) return { text: betreff, rohzeile: betreffRohzeile, label: 'Betreff' };
    if (selectedAnBlock === 'text' && paragraphen.length > 0) {
      const p = paragraphen[0]; return { text: p.text, rohzeile: p.rohzeile, label: 'Absatz 1' };
    }
    // Default: erster Absatz (immer sichtbar)
    if (paragraphen.length > 0) { const p = paragraphen[0]; return { text: p.text, rohzeile: p.rohzeile, label: 'Absatz 1' }; }
    if (betreff) return { text: betreff, rohzeile: betreffRohzeile, label: 'Betreff' };
    return null;
  })();

  // Nur wenn wirklich ein Blatt zu sehen ist, gibt es auch etwas zu zoomen.
  const zeigtDokument = status === 'fertig'
    || (status === null && (stelle.cv_data || stelle.cv_rohtext || stelle.generiert_am))
    || bautNurAnschreiben || bautNurCV || istLeererLebenslaufEntwurf;

  return (
    <>
    <div className="se-editor-wrapper">
    <div className="se-editor">
      {/* ── Hauptinhalt ─────────────────────────────── */}
      <div className="se-main">
      <div
        ref={scrollRef}
        className={`se-main-scroll${!text && zeigeFormGrid && !zeigeStatus ? ' se-main-scroll--center' : ''}`}
      >
{/* Abgeschickt: FitAnalyse oben, Stellenanzeige als Toggle */}
        {istAbgeschickt && (
          <div className="se-abgeschickt-section">
            <div className="se-fit-panel">
              <StatusCard
                status="fertig"
                schritt=""
                analyse={stelle.analyse || ''}
                analyseStruktur={analyseStruktur || stelle.analyse_struktur}
                cvText={cvTextExtrahieren(stelle)}
                onKenntnisseErgaenzen={onKenntnisseErgaenzen}
                klappbar
                startZu={bearbeitungsmodus}
              />
            </div>
            <KompakteStellenKarte
              text={text}
              url={url}
              modus={modus}
              stelleName={stelle?.name}
              isGenerating={false}
              onNeuGenerieren={modus => onAnalyseStarten(modus)}
            />
          </div>
        )}

        {/* FIT Analyse – volle Breite */}
        {zeigeStatus && (
          <StatusCard
            status={status}
            schritt={schritt}
            schrittIndex={schrittIndex}
            schrittTotal={schrittTotal}
            analyse={analyse}
            analyseStruktur={analyseStruktur}
            cvText={cvTextExtrahieren(stelle)}
            onKenntnisseErgaenzen={onKenntnisseErgaenzen}
            istTeilgenerierung={bautNurAnschreiben || bautNurCV}
          />
        )}

        {(bautBewerbung || bautNurAnschreiben || bautNurCV || status === 'fertig') && (
          <>
            <KompakteStellenKarte
              text={text}
              url={url}
              modus={modus}
              stelleName={stelle?.name}
              isGenerating={bautBewerbung || bautNurAnschreiben || bautNurCV}
              onAbbrechen={onAbbrechen}
            />
            {bautBewerbung && <CVGenerationSkeleton keywords={analyseKeywords} />}
          </>
        )}

        {/* Zwei-Spalten-Grid: Stellenbeschreibung + Aktionspanel */}
        {zeigeFormGrid && (
          <div className={`se-form-grid${!zeigeStatus ? ` se-form-grid--url-mode${showUrlInput ? '' : ' se-form-grid--text-mode'}` : ''}`}>
            {/* Linke Spalte */}
            <div className="se-form-left">
              <div className={`se-stellen-section se-editor-card se-source-card ${showUrlInput ? 'is-url' : 'is-text'}${zeigeStatus ? ' is-compact' : ''}`}>
                <div className="se-source-card-head">
                  <span className="se-stellen-eyebrow">Neue Stelle</span>
                  <h2 className="se-stellen-title">
                    {showUrlInput && textGeladen ? 'Stellenanzeige ausgelesen' : showUrlInput ? 'Link zur Stellenanzeige' : 'Stellenbeschreibung einfügen'}
                  </h2>
                  <p>
                    {showUrlInput && textGeladen
                      ? 'CVFlow hat die Anzeige erfolgreich ausgelesen. Du kannst den Text unten direkt einsehen oder bearbeiten.'
                      : showUrlInput
                      ? 'Füge zuerst den Link ein. CVFlow liest die Anzeige aus und bereitet die Analyse vor.'
                      : 'Nutze den Volltext, wenn kein stabiler Link verfügbar ist.'}
                  </p>
                </div>
                {showUrlInput && (
                  <div id="tutorial-se-url" className={`se-url-row${text ? '' : ' se-url-row-primary'}`}>
                    <input
                      className="se-url-input"
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        if (textGeladen) setText('');
                        setTextGeladen(false);
                      }}
                      placeholder="https://unternehmen.de/jobs/..."
                      autoFocus={!text}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !btnDisabled) btnAction();
                      }}
                      onPaste={(e) => {
                        const clip = (e.clipboardData || window.clipboardData)?.getData('text') ?? '';
                        if (!clip.trim()) return;
                        if (!/^https?:\/\//i.test(clip.trim()) && clip.trim().length > 40) {
                          e.preventDefault();
                          setText(clip.trim());
                          setModus('text');
                          setShowUrlInput(false);
                        }
                      }}
                    />
                  </div>
                )}
                {showUrlInput && textGeladen && text && (
                  <div id="tutorial-se-gelesen" className="se-inserat-view">
                    <div className="se-inserat-scroll">
                      {(() => {
                        const lines = text.split('\n');
                        const out = [];
                        for (let i = 0; i < lines.length; i++) {
                          const line = lines[i].replace(/\u00a0/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').trim();
                          if (!line) continue;
                          const prevBlank = i === 0 || !lines[i - 1].replace(/\u00a0/g, ' ').replace(/&nbsp;/g, ' ').trim();
                          const nextBlank = i >= lines.length - 1 || !lines[i + 1]?.replace(/\u00a0/g, ' ').replace(/&nbsp;/g, ' ').trim();
                          const isHeading = prevBlank && nextBlank && line.length <= 60 && !/[.!?,]$/.test(line) && !/^\d/.test(line);
                          out.push(isHeading
                            ? <h4 key={i} className="se-inserat-heading">{line}</h4>
                            : <p key={i} className="se-inserat-para">{line}</p>
                          );
                        }
                        return out;
                      })()}
                    </div>
                  </div>
                )}
                {!showUrlInput && (
                  <textarea
                    className="se-textarea"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Füge hier die vollständige Stellenbeschreibung ein..."
                    disabled={btnLaedt}
                    autoFocus
                  />
                )}
                <p className="se-stellen-tipp">
                  {showUrlInput && !text
                    ? 'Tipp: URLs von Jobbörsen oder Karriereseiten funktionieren am besten.'
                    : 'Tipp: Je vollständiger der Text, desto präziser werden CV und Anschreiben.'}
                </p>
                {showUrlInput && !text && url && (
                  <div className="se-inserat-laden">
                    <p>{textLaedt ? 'Stellenanzeige wird geladen…' : 'Stellenanzeige wird automatisch mitgeladen.'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rechte Spalte: Aktionskarte (sticky) */}
            <div className="se-form-right">
              <div id="tutorial-se-quellen" className="se-action-panel">
                <div className="se-action-panel-label">{(text || status) ? 'GEWÄHLTE QUELLE' : 'QUELLE WÄHLEN'}</div>
                <button
                  className={`se-source-option ${showUrlInput ? 'aktiv' : ''}`}
                  onClick={quelleAlsUrl}
                  disabled={btnLaedt}
                >
                  <LinkIcon />
                  <span>
                    <strong>URL verwenden</strong>
                    <small>Empfohlener Start</small>
                  </span>
                </button>
                <button
                  className={`se-source-option ${!showUrlInput ? 'aktiv' : ''}`}
                  onClick={quelleAlsText}
                  disabled={btnLaedt}
                >
                  <FileTextIcon size={16} />
                  <span>
                    <strong>Text einfügen</strong>
                    <small>Für kopierte Anzeigen</small>
                  </span>
                </button>
                <button
                  className="se-source-option"
                  onClick={() => pdfInputRef.current?.click()}
                  disabled={pdfLaedt || btnLaedt}
                >
                  <UploadIcon />
                  <span>
                    <strong>{pdfLaedt ? 'PDF wird gelesen…' : 'PDF importieren'}</strong>
                    <small>Anzeige als Datei</small>
                  </span>
                </button>
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={onStellenPdfUpload}
                />
                <div className="se-action-panel-divider" />
                <div id="tutorial-se-analyse" className="se-analyse-btn-wrap">
                <button
                  className={`se-analyse-btn${btnLaedt ? ' laedt' : ''}`}
                  onClick={() => btnAction()}
                  disabled={btnDisabled}
                >
                  {btnLabel}
                </button>
                </div>
                {(status === 'warten' || status === 'analyse' || status === 'generiert') && (
                  <button className="se-abort-btn" onClick={onAbbrechen}>Abbrechen</button>
                )}
              </div>
            </div>
          </div>
        )}

        {buildExit && (
          <CVGenerationSkeleton keywords={buildExitKeywords} exiting />
        )}

        {/* Fehler */}
        {(activeJob?.fehler || fehler) && <p className="se-fehler">{activeJob?.fehler || fehler}</p>}

        {/* Ergebnis */}
        {zeigtDokument && (
          <div className="se-ergebnis">
            {status === 'fertig' && (
              <p className="se-ergebnis-success">✓ Bewerbung erfolgreich generiert</p>
            )}
            {/* Die Zeile „Zuletzt generiert" stand hier und kostete eine ganze Zeilenhöhe
                über dem Dokument. Auf einem Laptop ist genau dieser Platz das, was dem
                Blatt an Größe fehlt — und wann zuletzt generiert wurde, sieht man auch
                am Dokument selbst. Auf Wunsch des Users am 2026-08-11 entfernt. */}
            <div style={{ display: aktivesDok === 'cv' ? 'block' : 'none' }}>
              <CVVorschau
                loading={bautNurCV}
                istLeererEntwurf={istLeererLebenslaufEntwurf}
                entwurfProfilName={profilName}
                keywords={analyseKeywords}
                stelle={stelle}
                dokumentOptionen={dokumentOptionen}
                editSektionIdx={editSektion?.idx}
                ref={cvRef}
                onEditToggle={onEditToggle}
                onProfilClick={(profilText) => onEditToggle(-1, { typ: 'profil', name: 'Zusammenfassung', profil_text: profilText })}
                onUndo={onCvUndo}
                onRedo={onCvRedo}
                canUndo={cvUndoStack.length > 0}
                canRedo={cvRedoStack.length > 0}
                undoCount={cvUndoStack.length}
                redoCount={cvRedoStack.length}
                onFotoChange={(fotoUrl) => {
                  onStelleGeaendert({ ...stelle, foto_url: fotoUrl }, 'update');
                }}
                onRebuildDone={(neuesDaten) => {
                  if (!neuesDaten) { onStelleGeaendert({ ...stelle }, 'update'); return; }
                  pushCv();
                  if (typeof neuesDaten === 'object' && !Array.isArray(neuesDaten) && neuesDaten.sektionen) {
                    onStelleGeaendert({ ...stelle, cv_data: neuesDaten }, 'update');
                    updateStelle(stelle.id, { cv_data: neuesDaten }).catch(speicherFehler);
                  } else {
                    onStelleGeaendert({ ...stelle, cv_rohtext: neuesDaten }, 'update');
                    updateStelle(stelle.id, { cv_rohtext: neuesDaten }).catch(speicherFehler);
                  }
                }}
                tabsElement={dokTabs}
                bearbeitungsmodus={bearbeitungsmodus}
                bearbeitenButton={bearbeitenButton}
                zoom={zoom}
                onFit={meldeCvFit}
              />
            </div>
            <div style={{ display: aktivesDok === 'an' ? 'block' : 'none' }}>
              {/* Der Hinweis auf mehrere Seiten ist am 2026-08-09 entfernt worden.
                  Er stammte aus der Zeit, als Seiten von selbst entstanden. Inzwischen
                  legt der User sie absichtlich an — der Hinweis meldete also einen
                  Normalzustand als Auffälligkeit. Gewarnt wird nur noch dort, wo wirklich
                  etwas nicht stimmt: unten am Blatt, wenn ein Block übersteht. */}
              {!bautNurAnschreiben && stelle?.qualitaet_warnung?.typ === 'anschreiben_substanz' && (
                <div className="se-qualitaet-banner">
                  <span className="se-qualitaet-banner-icon">⚠</span>
                  <span className="se-qualitaet-banner-text">
                    Anschreiben wurde mit Vorbehalt ausgeliefert — bitte prüfen.
                    {stelle.qualitaet_warnung.gruende?.length > 0 && (
                      <span className="se-qualitaet-banner-detail">
                        {' '}({stelle.qualitaet_warnung.gruende.join('; ')})
                      </span>
                    )}
                  </span>
                </div>
              )}
              <AnschreibenVorschau
                loading={bautNurAnschreiben}
                stelle={stelle}
                ref={anRef}
                onUndo={onAnUndo}
                onRedo={onAnRedo}
                canUndo={anUndoStack.length > 0}
                canRedo={anRedoStack.length > 0}
                undoCount={anUndoStack.length}
                redoCount={anRedoStack.length}
                onEditToggle={onAnEditToggle}
                onInlineChange={onSaveAnEdit}
                onLayoutChange={bearbeitungsmodus && aktivesDok === 'an' ? onAnLayoutChange : undefined}
                onBlockSelect={bearbeitungsmodus && aktivesDok === 'an' ? setSelectedAnBlock : undefined}
                onAbsenderToggle={onAbsenderToggle}
                onEmpfaengerToggle={onEmpfaengerToggle}
                onDatumToggle={onDatumToggle}
                onAbsenderInlineChange={onAbsenderInlineChange}
                onEmpfaengerInlineChange={onEmpfaengerInlineChange}
                onDatumInlineChange={onDatumInlineChange}
                tabsElement={dokTabs}
                bearbeitenButton={bearbeitenButton}
                zoom={zoom}
                onFit={meldeAnFit}
                leftPanel={EMPFAENGER_POPUPS.some(p => !stelle[p.key]) ? (
                  <div className="se-empf-hinweise">
                    {EMPFAENGER_POPUPS
                      .filter(p => !stelle[p.key])
                      .map(p => (
                        <div key={p.key} className={`se-empf-hinweis se-empf-hinweis--${p.severity}`} onClick={() => onEmpfaengerToggle()}>
                          <span className="se-empf-hinweis-label">{p.label} nicht gefunden</span>
                        </div>
                      ))
                    }
                  </div>
                ) : null}
              />
            </div>
            {status === 'fertig' && (
              <button className="se-abort-btn" style={{ marginTop: 16 }} onClick={onAbbrechen}>
                Nochmal generieren
              </button>
            )}
          </div>
        )}

        {/* Background status watermark */}
        {aktiverStatusOpt && (
          <div className="se-bg-status-wm" style={{ '--wm-color': aktiverStatusOpt.color }}>
            <aktiverStatusOpt.Icon size={280} color={aktiverStatusOpt.color} />
          </div>
        )}

        {/* Powered By Footer */}
        <div className="se-powered-by">
          <span>POWERED BY ANTHROPIC</span>
        </div>
      </div>

      {/* Der Regler hängt am Rahmen, nicht an der scrollenden Insel — sonst wanderte er
          beim Scrollen aus dem Bild und nähme dem Blatt zusätzlich Platz weg. */}
      {zeigtDokument && (
        <ZoomRegler
          wert={zeigtSkala}
          auto={zoom === null}
          onZoom={setZoom}
          onAuto={() => setZoom(null)}
        />
      )}
      </div>

    </div>
    </div>

    {/* ── Status FAB ── */}
    {stelle && (status === 'fertig' || (status === null && stelle.generiert_am) || !!aktiverStatusOpt) && ReactDOM.createPortal(
      <motion.div
        className="se-status-fab-root"
        style={{ right: fabRight }}
        ref={statusFabRef}
      >
        {statusDropdownOffen && (
          <div className="se-status-fab-menu">
            {STATUS_OPTIONEN.map(opt => (
              <button
                key={opt.key}
                className={`se-status-fab-item${bewStatus === opt.key ? ' aktiv' : ''}`}
                onClick={() => onBewStatusAendern(opt.key)}
                style={{ '--item-color': opt.color }}
              >
                <opt.Icon size={18} color={opt.color} />
                <span>{opt.label}</span>
                {bewStatus === opt.key && <span className="se-status-fab-check">✓</span>}
              </button>
            ))}
          </div>
        )}
        <button
          className={`se-status-fab-btn${aktiverStatusOpt ? ' hat-status' : ''}`}
          onClick={() => setStatusDropdownOffen(v => !v)}
          style={aktiverStatusOpt ? { '--fab-color': aktiverStatusOpt.color } : {}}
        >
          {aktiverStatusOpt ? (
            <>
              <aktiverStatusOpt.Icon size={16} color={aktiverStatusOpt.color} />
              <span>{aktiverStatusOpt.label}</span>
            </>
          ) : (
            <>
              <span className="se-status-fab-track-icon">●</span>
              <span>Verfolgen</span>
            </>
          )}
          <span className={`se-status-fab-arrow${statusDropdownOffen ? ' offen' : ''}`}>▲</span>
        </button>
      </motion.div>,
      document.body
    )}

    {/* ── Stamp Animation ── */}
    {stampAnzeige && ReactDOM.createPortal(
      (() => {
        const opt = STATUS_OPTIONEN.find(o => o.key === stampAnzeige);
        if (!opt) return null;
        return (
          <div className="se-stamp-overlay">
            <div className="se-stamp-inner" style={{ '--stamp-color': opt.color }}>
              <div className="se-stamp-icon-wrap">
                <opt.Icon size={64} color={opt.color} />
              </div>
              <span className="se-stamp-label">{opt.label}</span>
            </div>
          </div>
        );
      })(),
      document.body
    )}


    {/* ── Rechtes Panel via Portal ── */}
    {ReactDOM.createPortal(
      <div className="ssp-slot-wrapper">
        {/* Basis-Insel: immer gemountet, wird nach links geschoben wenn Edit-Panel offen */}
        <div className={`ssp-base-layer${baseLayerPushed ? ' is-behind' : ''}`}>
          <DokumentePanel
            stelle={stelle}
            cvVorlagen={cvVorlagen}
            onVorlageChange={onVorlageChange}
            onDownloadCV={druckCV}
            onDownloadAN={druckAN}
            onBlobCV={cvBlobUrl}
            onBlobAN={anBlobUrl}
            cvLaedt={pdfLaedt2.cv}
            anLaedt={pdfLaedt2.an}
            defaultMappeOffen={defaultMappeOffen}
            onAlsAbgeschickt={onAlsAbgeschickt}
            editSektion={editSektion}
            sektionPanelRef={sektionPanelRef}
            onSaveEdit={onSaveEdit}
            onLiveSektion={onLiveSektion}
            onPanelClose={onPanelClose}
            onPanelClosingStart={onPanelClosingStart}
            inlineAnItem={inlineAnItem}
            stelleId={stelle?.id}
            profil={aktuellesProfil}
            onSaveAnEdit={onSaveAnEdit}
          />
        </div>

        {/* Anschreiben Absatz-Panel */}
        {editAnItem && (
          <AnschreibenAbsatzPanel
            key={editAnItem.item.rohzeile}
            ref={anPanelRef}
            item={editAnItem.item}
            anRohtext={editAnItem.anRohtext}
            stelleId={stelle?.id}
            profil={editAnItem.profil}
            onSave={onSaveAnEdit}
            onClose={onAnPanelClose}
            onClosingStart={onAnPanelClosingStart}
          />
        )}

        {/* Anschreiben Absender-Panel */}
        {editAbsender && (
          <AnschreibenAbsenderPanel
            key="absender"
            ref={absenderPanelRef}
            stelle={stelle}
            profil={aktuellesProfil}
            onSave={onSaveAbsender}
            onClose={onAbsenderPanelClose}
            onClosingStart={onAbsenderPanelClosingStart}
          />
        )}

        {/* Anschreiben Empfänger-Panel */}
        {editEmpfaenger && (
          <AnschreibenEmpfaengerPanel
            key="empfaenger"
            ref={empfaengerPanelRef}
            stelle={stelle}
            onSave={onSaveEmpfaenger}
            onClose={onEmpfaengerPanelClose}
            onClosingStart={onEmpfaengerPanelClosingStart}
          />
        )}

        {/* Anschreiben Datum-Panel */}
        {editDatum && (
          <AnschreibenDatumPanel
            key="datum"
            ref={datumPanelRef}
            stelle={stelle}
            profil={aktuellesProfil}
            onSave={onSaveDatum}
            onClose={onDatumPanelClose}
            onClosingStart={onDatumPanelClosingStart}
          />
        )}
      </div>,
      document.getElementById('editor-panel-slot') || document.body
    )}
    </>
  );
}

export default StellenEditor;
